import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  FaFemale,
  FaMale,
} from 'react-icons/fa';
import {
  LuCalendarDays,
  LuBookmark,
  LuCheck,
  LuHeart,
  LuMapPin,
  LuMessageCircle,
  LuSend,
  LuUsersRound,
} from 'react-icons/lu';

const STEPS = [
  {
    type: 'lesson',
    narration:
      'Now that you understand your role, the next three sections will show you exactly how to identify and reach the right people. First, we’ll define who you should target. Next, we’ll show you where to find them. Then, we’ll walk through how to contact them using the outreach script.',
  },
  {
    type: 'lesson',
    narration:
      'Your first responsibility is deciding who to contact. Focus on people who live in Los Angeles and are typically between twenty and twenty-nine years old. Tribe is working toward a membership mix of roughly seventy percent women and thirty percent men, so most of your outreach should be directed toward women. Strong prospects are also usually single or dating rather than married, and typically do not have children.',
  },
  {
  type: 'question',
  prompt:
    'Which person should you prioritize for outreach?',
  choices: [
    {
      value: 'A',
      label:
        'A 25-year-old woman in San Diego who wants to meet more people locally',
    },
    {
      value: 'B',
      label:
        'A 24-year-old woman in Los Angeles who recently moved and wants to build a new social circle',
    },
    {
      value: 'C',
      label:
        'A 27-year-old woman in Los Angeles who is looking primarily for activities for parents and children',
    },
  ],
  correct: 'B',
  explanation:
    'Prioritize prospects in their twenties who live in Los Angeles and have a clear reason for wanting to build a new social circle.',
},
  {
    type: 'lesson',
    narration:
      'Age, location, gender, and life stage are only the starting point. We’re looking for people who are comfortable interacting with others but need a new circle because they recently moved to Los Angeles, started a new job or school, entered a new phase of life, or simply want to meet more people. Tribe is designed for loneliness caused by circumstance—not for someone who needs significant support navigating basic social interactions. The right person should be able to contribute positively to the group.',
  },
  {
    type: 'lesson',
    narration:
      'So how can you spot a strong prospect from their profile? First, estimate whether they are between twenty and twenty-nine. Their age may be listed in their bio, or you may be able to estimate it from their appearance or college graduation year. Their bio may mention recently moving to Los Angeles, starting a new job or school, or wanting to meet new people. Look for photos that show a good sense of style, time spent with friends, and comfort in social settings—not only selfies. Finally, look for signs that they are in the right life stage, such as no mention of being married or having children and no photos suggesting they are a parent. These are signals to guide your outreach, not details you need to verify with them.',
  },
  {
    type: 'question',
    prompt: 'Profile prospect game',
    game: true,
    correct: 'profile-game-complete',
    explanation:
      'Complete all eight profiles to finish this section.',
  },
];

const PART_TIMINGS = {
  0: [2400, 4800, 8200, 11200, 14200, 17700],
  1: [3100, 7200, 10900, 18400, 25800],
  3: [9500, 11000, 12500, 14000, 17000],
  4: [3500, 16000, 24500, 31000],
};

const TARGETING_STEPS = [
  {
    number: '01',
    action: 'Who',
    remainder: 'to target',
  },
  {
    number: '02',
    action: 'Where',
    remainder: 'to find them',
  },
  {
    number: '03',
    action: 'How',
    remainder: 'to contact them',
  },
];


const FIT_REASONS = [
  {
    title: 'Recently moved',
    detail: 'They are building a social life in a new city.',
  },
  {
    title: 'New job or school',
    detail: 'Their daily environment recently changed.',
  },
  {
    title: 'New phase of life',
    detail: 'Their existing social routine no longer fits.',
  },
  {
    title: 'Wants to meet more people',
    detail: 'They are ready to expand beyond their current circle.',
  },
];

const FINAL_FIT_STATEMENT =
  'The right person should contribute positively to the group.';

const FIT_REASON_CAPTIONS = [
  'because they recently moved to Los Angeles,',
  'started a new job or school,',
  'entered a new phase of life,',
  'or simply want to meet more people.',
];

const FINAL_FIT_ICONS = [
  LuMapPin,
  LuCalendarDays,
  LuHeart,
  LuUsersRound,
];

const INSTAGRAM_PHOTOS = [
  {
    src: 'https://i.pinimg.com/736x/e2/29/76/e22976c6e30d837694dc64bb64cf6df5.jpg',
    alt: 'Maya profile photo one',
  },
  {
    src: 'https://i.pinimg.com/736x/3f/5b/d9/3f5bd9d873a2894797d9c8ea3800ad8b.jpg',
    alt: 'Maya profile photo two',
  },
  {
    src: 'https://i.pinimg.com/736x/ed/3d/6b/ed3d6bac43d643e86bd6616d853b7dbd.jpg',
    alt: 'Maya profile photo three',
  },
  {
    src: 'https://i.pinimg.com/736x/a0/8d/78/a08d7883d28fc74405f52ecd315e54ce.jpg',
    alt: 'Maya profile photo four',
  },
  {
    src: 'https://i.pinimg.com/736x/0b/98/ab/0b98ab4a4bc41452f4b49106b0efec7d.jpg',
    alt: 'Maya profile photo five',
  },
  {
    src: 'https://i.pinimg.com/736x/4b/f2/36/4bf23614748e0f0021be643258c8185c.jpg',
    alt: 'Maya profile photo six',
  },
];

const INSTAGRAM_PROFILE_PHOTO =
  'https://i.pinimg.com/1200x/b8/8d/6f/b88d6f9bc27f967e569640c198e6ba8a.jpg';

const FIT_KINETIC_CAPTIONS = [
  {
    text: 'Age, location, gender, and life stage',
    at: 100,
    duration: 2400,
  },
  {
    text: 'are only the starting point.',
    at: 2500,
    duration: 1600,
  },
  {
    text: 'We’re looking for people',
    at: 4100,
    duration: 1500,
  },
  {
    text: 'who are comfortable interacting with others',
    at: 5600,
    duration: 2000,
  },
  {
    text: 'but need a new circle',
    at: 7600,
    duration: 1400,
  },
];

const PROFILE_SIGNALS = [
  {
    key: 'age',
    title: 'Estimate their age',
    detail:
      'Use clues in their bio, photos, or graduation year.',
  },
  {
    key: 'bio',
    title: 'Identify why they may want to meet new people',
    detail:
      'Look for a recent move, new job or school, or interest in expanding their social life.',
  },
  {
    key: 'photos',
    title: 'Assess style + social comfort',
    detail:
      'Look for personal style, activities, and photos with friends.',
  },
  {
    key: 'life',
    title: 'Check their life stage',
    detail:
      'Look for signs that they are single or dating and do not have children.',
  },
];

const PROSPECT_PROFILES = [
  {
    name: 'Maya',
    handle: 'maya.young',
    age: 28,
    location: 'Los Angeles',
    initials: 'MY',
    photo: 'https://i.pinimg.com/1200x/b8/8d/6f/b88d6f9bc27f967e569640c198e6ba8a.jpg',
    bio: 'Recently moved to LA for work. Into thrifting, concerts, dinner parties, and trying new restaurants.',
    snapshot: ['Recently moved', 'Photos with friends', 'Exploring LA'],
    goodProspect: true,
    feedback:
      'Maya is in the target age range, lives in LA, appears comfortable with people, and has a clear reason to build a new circle.',
  },
  {
    name: 'Nicole',
    handle: 'nicole.c',
    age: 25,
    location: 'San Diego',
    initials: 'NC',
    photo: 'https://i.pinimg.com/736x/21/a0/ab/21a0ab55d366ce6e869ebf5c6f2e9912.jpg',
    bio: 'Always looking for new restaurants, concerts, and people to explore the city with.',
    snapshot: ['Photos with friends', 'Concerts + restaurants', 'Based in San Diego'],
    goodProspect: false,
    feedback:
      'Nicole otherwise looks like a strong prospect, but she lives outside Los Angeles. Tribe currently operates in LA.',
  },
  {
    name: 'Taylor',
    handle: 'taylor.l',
    age: 23,
    location: 'Los Angeles',
    initials: 'TL',
    photo: 'https://i.pinimg.com/736x/8d/6f/a7/8d6fa75779579ffcc5b3586a8f4049d3.jpg',
    bio: 'LA. Music + fashion.',
    snapshot: ['Unkempt presentation', 'Only solo selfies', 'No friends or activities shown'],
    goodProspect: false,
    feedback:
      'Taylor meets the age and location targets, but the profile only shows unkempt solo selfies with no friends, activities, or social life shown.',
  },
  {
    name: 'Avery',
    handle: 'avery.in.la',
    age: 32,
    location: 'Los Angeles',
    initials: 'AV',
    photo: 'https://i.pinimg.com/1200x/f0/94/f6/f094f6789f631d32dc848ca4a934a31f.jpg',
    bio: 'New to LA and looking for people to explore restaurants, museums, and nightlife with.',
    snapshot: ['Recently moved', 'Photos with friends', 'Age 32'],
    goodProspect: true,
    feedback:
      'Avery is slightly outside the primary age range, but the other signals are strong. Ages 20–29 are the priority, not a strict cutoff.',
  },
  {
    name: 'Emma',
    handle: 'emma.west',
    age: 20,
    location: 'Los Angeles',
    initials: 'EM',
    photo: 'https://i.pinimg.com/736x/5b/fc/30/5bfc30bdf23945bb362885303206c0f0.jpg',
    bio: 'Just started school in LA. Looking for people to explore the city and try new things with.',
    snapshot: ['Started school in LA', 'Photos with friends', 'Exploring LA'],
    goodProspect: true,
    feedback:
      'Emma is within the target age range and has a clear circumstantial reason to build a new circle in Los Angeles.',
  },
  {
    name: 'Chloe',
    handle: 'chloe.living',
    age: 27,
    location: 'Los Angeles',
    initials: 'CH',
    photo: 'https://i.pinimg.com/736x/29/71/39/2971397d96548e833a91a87eaf8419d2.jpg',
    bio: 'Wife, mom, and lifestyle creator. Looking for activities for parents and young children.',
    snapshot: ['Married', 'Has children', 'Family-focused activities'],
    goodProspect: false,
    feedback:
      'Chloe meets the age and location targets, but she is outside the life stage Tribe is primarily targeting.',
  },
  {
    name: 'Jasmine',
    handle: 'jasmine.m',
    age: 29,
    location: 'Los Angeles',
    initials: 'JM',
    photo: 'https://i.pinimg.com/1200x/60/47/29/604729cdc84238b71eda9719fe97b3e5.jpg',
    bio: 'Starting a new chapter and trying to say yes to more things. Fashion, live music, museums, and good food.',
    snapshot: ['New chapter', 'Photos with friends', 'Shared interests'],
    goodProspect: true,
    feedback:
      'Jasmine fits the target demographic, appears comfortable with people, and has a clear reason to expand her circle.',
  },
  {
    name: 'Marcus',
    handle: 'marcus.cole',
    age: 26,
    location: 'Los Angeles',
    initials: 'MC',
    photo: 'https://i.pinimg.com/736x/ce/95/06/ce9506cb2b8110ced1b4b7679a148675.jpg',
    bio: 'Moved to LA for a new job. Looking for friends for concerts, pickup games, and new restaurants.',
    snapshot: ['Recently moved', 'Photos with friends', 'Interested in activities'],
    goodProspect: true,
    feedback:
      'Marcus is a strong prospect. Women should receive most of your outreach, but men are not excluded from Tribe.',
  },
];

export default function WhoToInvite({
  part,
  Question,
  selected,
  onSelect,
  audioStatus,
  playbackId,
}) {
  const [visualStage, setVisualStage] =
    useState(0);
  const step = STEPS[part];

  useEffect(() => {
    if (
      step?.type !== 'lesson' ||
      audioStatus !== 'Playing'
    ) {
      return undefined;
    }

    setVisualStage(0);

    const timers = (
      PART_TIMINGS[part] || []
    ).map((time, index) =>
      window.setTimeout(
        () => setVisualStage(index + 1),
        time
      )
    );

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [
    part,
    step?.type,
    audioStatus,
    playbackId,
  ]);

  useEffect(() => {
    if (
      step?.type === 'lesson' &&
      audioStatus === 'Complete'
    ) {
      setVisualStage(
        (PART_TIMINGS[part] || []).length
      );
    }
  }, [part, step?.type, audioStatus]);

  if (
    step.type === 'question' &&
    part !== 5
  ) {
    return (
      <Question
        {...step}
        selected={selected}
        onSelect={onSelect}
      />
    );
  }

  return (
    <>
      <style>{STYLES}</style>

      {part === 0 && (
        <TargetingOverview
          stage={visualStage}
        />
      )}

      {part === 1 && (
        <BasicTarget
          stage={visualStage}
          playbackActive={
            audioStatus === 'Playing' ||
            audioStatus === 'Complete'
          }
        />
      )}

      {part === 3 && (
        <DeeperFit stage={visualStage} />
      )}

      {part === 4 && (
        <ProfileSignals
          stage={visualStage}
        />
      )}

      {part === 5 && (
        <ProspectGame
          onComplete={() =>
            onSelect(
              'profile-game-complete',
              'profile-game-complete'
            )
          }
        />
      )}
    </>
  );
}

function TargetingOverview({ stage }) {
  const currentStage = Math.min(stage, 6);

  if (currentStage < 3) {
    return (
      <section className="who-slide overview-slide">
        <div
          className="overview-intro-beat"
          key={currentStage}
        >
          {currentStage === 0 ? (
            <OverviewWords
              text="Now that you understand your role,"
              wordDelay={280}
            />
          ) : currentStage === 1 ? (
            <OverviewWords
              text="the next three sections will show you exactly"
              wordDelay={210}
            />
          ) : (
            <div className="overview-intro-line">
              <OverviewWords
                text="how to identify and reach the"
                wordDelay={280}
              />

              <span className="overview-intro-handwritten">
                <OverviewWords
                  text="right people."
                  delay={2050}
                  wordDelay={280}
                />
              </span>
            </div>
          )}
        </div>
      </section>
    );
  }

  const visibleCount = Math.min(currentStage - 2, 3);
  const reviewing = currentStage >= 6;

  return (
    <section className="who-slide overview-slide">
      <div
        className={[
          'overview-step-stack',
          reviewing ? 'is-reviewing' : '',
        ].join(' ')}
      >
        {TARGETING_STEPS.map((item, index) => {
          const visible = index < visibleCount;
          const current = index === visibleCount - 1;
          const complete = visible && index < visibleCount - 1;

          return (
            <div
              className={[
                'overview-step-row',
                visible ? 'is-visible' : '',
                complete || reviewing ? 'is-complete' : '',
                current && !reviewing ? 'is-current' : 'is-previous',
              ].join(' ')}
              key={item.number}
            >
              <span
                className="overview-step-marker"
                aria-hidden="true"
              >
                {complete || reviewing ? '✓' : ''}
              </span>

              <div className="overview-step-copy">
                <strong>
                  <span className="overview-step-action">
                    {item.action}
                  </span>{' '}
                  {item.remainder}
                </strong>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function OverviewWords({
  text,
  delay = 0,
  wordDelay = 90,
}) {
  const words = text.split(' ');

  return (
    <span
      className="overview-kinetic-words"
      aria-label={text}
    >
      {words.map((word, index) => (
        <React.Fragment key={`${word}-${index}`}>
          <span
            style={{
              '--overview-word-delay': `${
                delay + index * wordDelay
              }ms`,
            }}
            aria-hidden="true"
          >
            {word}
          </span>

          {index < words.length - 1 ? ' ' : ''}
        </React.Fragment>
      ))}
    </span>
  );
}

function BasicTarget({ stage, playbackActive }) {
  const californiaPathRef = useRef(null);
  const traceLineRef = useRef(null);
  const traceHeadRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [locationFinished, setLocationFinished] =
    useState(false);

  const showingIntro =
    playbackActive && stage === 0;
  const showingReview =
    playbackActive && stage >= 5;

  const currentScene =
    stage >= 4
      ? 3
      : stage >= 3
        ? 2
        : stage >= 2
          ? 1
          : 0;

  const sceneClass = (index) => {
    if (!playbackActive) {
      return 'is-after';
    }

    if (showingIntro) {
      return 'is-after';
    }

    if (showingReview) {
      return 'is-before';
    }

    if (index === currentScene) {
      return 'is-current';
    }

    if (index < currentScene) {
      return 'is-before';
    }

    return 'is-after';
  };

  useEffect(() => {
    const path = californiaPathRef.current;
    const traceLine = traceLineRef.current;
    const traceHead = traceHeadRef.current;

    if (!path || !traceLine || !traceHead) {
      return undefined;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(
        animationFrameRef.current
      );
    }

    traceLine.setAttribute('points', '');
    traceHead.style.opacity = '0';
    setLocationFinished(false);

    if (stage < 1 || currentScene !== 0) {
      return undefined;
    }

    const totalLength =
      path.getTotalLength();
    const startingPoint =
      path.getPointAtLength(0);
    const holdDuration = 120;
    const traceDuration = 1500;
    const startedAt = performance.now();

    traceHead.style.opacity = '1';
    traceHead.setAttribute(
      'transform',
      `translate(${startingPoint.x} ${startingPoint.y})`
    );

    const drawFrame = (now) => {
      const elapsed = now - startedAt;
      const traceElapsed = Math.max(
        0,
        elapsed - holdDuration
      );
      const progress = Math.min(
        traceElapsed / traceDuration,
        1
      );
      const currentLength =
        totalLength * progress;
      const point =
        path.getPointAtLength(currentLength);
      const points = [];

      for (
        let distance = 0;
        distance < currentLength;
        distance += 1.5
      ) {
        const sample =
          path.getPointAtLength(distance);
        points.push(
          `${sample.x},${sample.y}`
        );
      }

      points.push(`${point.x},${point.y}`);
      traceLine.setAttribute(
        'points',
        points.join(' ')
      );
      traceHead.setAttribute(
        'transform',
        `translate(${point.x} ${point.y}) scale(${
          1 - progress * 0.82
        })`
      );

      const fadeProgress = Math.max(
        0,
        (progress - 0.78) / 0.22
      );

      traceHead.style.opacity = `${
        1 - fadeProgress
      }`;

      if (progress < 1) {
        animationFrameRef.current =
          requestAnimationFrame(drawFrame);
        return;
      }

      traceHead.style.opacity = '0';
      setLocationFinished(true);
    };

    animationFrameRef.current =
      requestAnimationFrame(drawFrame);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(
          animationFrameRef.current
        );
      }
    };
  }, [stage, currentScene]);

  return (
    <section className="who-slide target-slide">
      <div className="target-scene-stage">
        <div
          className={[
            'target-intro',
            showingIntro ? 'is-visible' : '',
          ].join(' ')}
        >
          <h1
            aria-label="Your first responsibility is deciding who to contact."
          >
            {[
              'Your',
              'first',
              'responsibility',
              'is',
              'deciding',
            ].map((word, index) => (
              <React.Fragment key={word}>
                <span
                  className="target-intro-word"
                  style={{
                    '--target-word-delay': `${index * 245}ms`,
                  }}
                  aria-hidden="true"
                >
                  {word}
                </span>
                {index < 4 ? ' ' : ''}
              </React.Fragment>
            ))}

            {' '}

            <span className="target-intro-hand-group">
              {['who', 'to', 'contact.'].map(
                (word, index) => (
                  <React.Fragment key={word}>
                    <span
                      className="target-intro-word"
                      style={{
                        '--target-word-delay': `${
                          (index + 5) * 245
                        }ms`,
                      }}
                      aria-hidden="true"
                    >
                      {word}
                    </span>
                    {index < 2 ? ' ' : ''}
                  </React.Fragment>
                )
              )}
            </span>
          </h1>
        </div>

        <div
          className={[
            'target-scene location-scene',
            sceneClass(0),
          ].join(' ')}
        >
          <div className="la-map" aria-hidden="true">
            <svg viewBox="185 245 145 250">
              <g className="california-shape">
                <path
                  ref={californiaPathRef}
                  className="california-guide"
                  d="M310.7 486.8l3.8-.5 1.5-2 .7-1.9-3.2-.1-1.1-1.8.8-1.7v-6.2l2.2-1.3 2.7-2.6.4-4.9 1.6-3.5 1.9-2.1 3.3-1.7 1.3-.7.8-1.5-.9-.9-1-1.5-.9-5.3-2.9-5.2.1-2.8-2.2-3.2-15-23.2-19.4-28.7-22.4-33-12.7-19.5 1.8-7.2 6.8-25.9 8.1-31.4-12.4-3.3-13.5-3.4-12.6-4.1-7.5-2.1-11.4-3-7.1-2.4-1.6 4.7-.2 7.4-5.2 11.8-3.1 2.6-.3 1.1-1.8.8-1.5 4.2-.8 3.2 2.7 4.2 1.6 4.2 1.1 3.6-.3 6.5-1.8 3.1-.6 5.8-1 3.7 1.8 3.9 2.7 4.5 2.3 4.8 1.3 4-.3 3.2-.3.5v2.1l5.7 6.3-.5 2.4-.6 2.3-.6 1.9.2 8.2 2.1 3.7 1.9 2.6 2.7.5 1 2.7-1.1 3.6-2.1 1.6h-1.1l-.8 3.9.5 2.9 3.2 4.4 1.6 5.3 1.5 4.7 1.3 3.1 3.4 5.8 1.5 2.6.5 2.9 1.6 1v2.4l-.8 1.9-1.8 7.1-.5 1.9 2.4 2.7 4.2.5 4.5 1.8 3.9 2.1h2.9l2.9 3.1 2.6 4.8 1.1 2.3 3.9 2.1 4.8.8 1.5 2.1.6 3.2-1.5.6.3 1 3.2.8 2.7.2 2.9 4.7 3.9 4.2.8 2.3 2.6 4.2.3 3.2v9.4l.5 1.8 10 1.5 19.7 2.7Z"
                />
                <polyline
                  ref={traceLineRef}
                  className="california-outline"
                />
                <g
                  ref={traceHeadRef}
                  className="california-trace-head"
                >
                  <circle
                    className="trace-head-pulse"
                    r="9"
                  />
                  <circle
                    className="trace-head-dot"
                    r="4.5"
                  />
                </g>
                <g
                  className={[
                    'los-angeles-marker',
                    locationFinished
                      ? 'is-visible'
                      : '',
                  ].join(' ')}
                >
                  <circle
                    className="la-pulse"
                    cx="255"
                    cy="442"
                    r="8"
                  />
                  <circle
                    className="la-dot"
                    cx="255"
                    cy="442"
                    r="4.5"
                  />
                </g>

                <g
                  className={[
                    'la-handwritten-callout',
                    locationFinished
                      ? 'is-visible'
                      : '',
                  ].join(' ')}
                  transform="translate(-8 10)"
                >
                  <path
                    className="la-callout-arrow la-callout-curve"
                    pathLength="1"
                    d="M204 470 C219 465 238 454 248 446"
                  />
                  <path
                    className="la-callout-arrow la-callout-head"
                    pathLength="1"
                    d="M241 444 L250 445 L246 453"
                  />
                  <text
                    className="la-callout-text"
                    x="200"
                    y="489"
                    textAnchor="end"
                  >
                    LOS ANGELES
                  </text>
                </g>
              </g>
            </svg>
          </div>
        </div>

        <div
          className={[
            'target-scene age-scene',
            sceneClass(1),
          ].join(' ')}
        >
          <div
            className="age-visual"
            aria-label="Typically 20 to 29 years old"
          >
            <div className="age-range-copy">
              <strong>20</strong>
              <span
                className="age-range-dash"
                aria-hidden="true"
              />
              <strong>29</strong>
            </div>

            <div
              className="age-scale"
              aria-hidden="true"
            >
              <i />
              <i />
              <i />
              <i />
              <i />
            </div>

            <span>Typically in their twenties</span>
          </div>
        </div>

        <div
          className={[
            'target-scene audience-scene',
            sceneClass(2),
          ].join(' ')}
        >
          <div
            className="audience-ratio"
            aria-label="Target mix: 70 percent women and 30 percent men"
          >
            <div className="ratio-row female-stat">
              <FaFemale aria-hidden="true" />
              <span className="ratio-track">
                <i />
              </span>
              <strong>70%</strong>
              <small>Women</small>
            </div>

            <div className="ratio-row male-stat">
              <FaMale aria-hidden="true" />
              <span className="ratio-track">
                <i />
              </span>
              <strong>30%</strong>
              <small>Men</small>
            </div>
          </div>
        </div>

        <div
          className={[
            'target-scene life-stage-scene',
            sceneClass(3),
          ].join(' ')}
        >
          <div
            className="life-stage-type"
            aria-label="Typically single or dating, not married, and without children"
          >
            {[
              'Single or dating',
              'Not married',
              'No children',
            ].map((statement, index) => (
              <strong
                className="life-stage-statement"
                style={{
                  '--life-stage-delay': `${120 + index * 360}ms`,
                }}
                key={statement}
                aria-hidden="true"
              >
                {statement}
              </strong>
            ))}
          </div>
        </div>

        <div
          className={[
            'target-review',
            showingReview ? 'is-visible' : '',
          ].join(' ')}
        >
          <h2>IDEAL MEMBER OVERVIEW</h2>

          <div className="target-review-quadrants">
            <div className="target-review-quadrant">
              <LuMapPin
                aria-hidden="true"
                strokeWidth={1.65}
              />
              <strong>Los Angeles only</strong>
              <small>CURRENT SERVICE AREA</small>
            </div>

            <div className="target-review-quadrant">
              <LuCalendarDays
                aria-hidden="true"
                strokeWidth={1.65}
              />
              <strong>In their 20s</strong>
              <small>TYPICALLY 20–29</small>
            </div>

            <div className="target-review-quadrant">
              <FaFemale
                className="review-female-icon"
                aria-hidden="true"
              />
              <strong>Mostly women</strong>
              <small>FOCUS MOST OUTREACH HERE</small>
            </div>

            <div className="target-review-quadrant">
              <LuHeart
                aria-hidden="true"
                strokeWidth={1.65}
              />
              <strong>Single / dating</strong>
              <small>NOT MARRIED · NO KIDS</small>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DeeperFit({ stage }) {
  if (stage === 0) {
    return (
      <section className="who-slide fit-slide">
        <KineticFitIntro />
      </section>
    );
  }

  return (
    <FitFinalStatement
      activeIndex={Math.min(stage - 1, 3)}
      review={stage >= 5}
    />
  );
}

function FitFinalStatement({
  activeIndex,
  review,
}) {
  const runningCaption = review
    ? FINAL_FIT_STATEMENT
    : FIT_REASON_CAPTIONS[activeIndex];

  return (
    <section className="who-slide fit-slide">
      <div className="fit-final-summary">
        <div className="fit-final-list">
          {FIT_REASONS.map((reason, index) => {
            const Icon = FINAL_FIT_ICONS[index];
            const active = index === activeIndex;

            return (
              <div
                className={[
                  'fit-final-list-item',
                  active ? 'is-active' : '',
                  review ? 'is-review' : 'is-muted',
                ].join(' ')}
                key={reason.title}
              >
                <Icon aria-hidden="true" />
                <strong>{reason.title}</strong>
              </div>
            );
          })}
        </div>

        <i
          className="fit-final-divider"
          aria-hidden="true"
        />

        <div
          className="fit-final-message"
          aria-label={runningCaption}
          key={runningCaption}
        >
          {runningCaption.split(' ').map(
            (word, index, words) => (
              <React.Fragment key={`${word}-${index}`}>
                <span
                  className={
                    word.replace(/[.,]/g, '') === 'positively'
                      ? 'fit-summary-handwritten'
                      : ''
                  }
                  style={{
                    '--fit-summary-word-delay': `${
                      180 + index * 75
                    }ms`,
                  }}
                  aria-hidden="true"
                >
                  {word}
                </span>

                {index < words.length - 1 ? ' ' : ''}
              </React.Fragment>
            )
          )}
        </div>
      </div>
    </section>
  );
}

function KineticFitIntro() {
  const fullText = FIT_KINETIC_CAPTIONS.map(
    (caption) => caption.text
  ).join(' ');

  return (
    <div
      className="fit-kinetic-sentence"
      aria-label={fullText}
    >
      {FIT_KINETIC_CAPTIONS.map(
        (caption) => {
          const words = caption.text.split(' ');

          return (
            <div
              className="fit-caption-beat"
              style={{
                '--fit-caption-delay': `${caption.at}ms`,
                '--fit-caption-duration': `${caption.duration}ms`,
              }}
              key={caption.text}
              aria-hidden="true"
            >
              {words.map((word, wordIndex) => (
                <React.Fragment
                  key={`${word}-${wordIndex}`}
                >
                  <span
                    className={
                      caption.text ===
                        'are only the starting point.' &&
                      wordIndex >= 3
                        ? 'fit-starting-point-hand'
                        : ''
                    }
                    style={{
                      '--fit-word-delay': `${
                        caption.at +
                        100 +
                        wordIndex * 145
                      }ms`,
                    }}
                  >
                    {word}
                  </span>

                  {wordIndex < words.length - 1
                    ? ' '
                    : ''}
                </React.Fragment>
              ))}
            </div>
          );
        }
      )}
    </div>
  );
}

function FitReasonIllustration({ index }) {
  if (index === 0) {
    return (
      <div className="fit-reason-visual">
        <svg
          className="fit-editorial-art fit-arrival-art"
          viewBox="0 0 420 300"
          role="img"
          aria-label="A rolling suitcase arriving in Los Angeles"
        >
          <g className="fit-suitcase-object">
            <path
              className="fit-ink fit-draw fit-handle"
              pathLength="1"
              d="M111 112 C111 83 113 52 118 39 C121 31 128 28 139 29 L166 31 C177 32 182 37 181 47 L178 108"
            />
            <path
              className="fit-ink fit-draw fit-handle-inner"
              pathLength="1"
              d="M122 105 L124 48 C125 43 129 41 136 41 L165 43 C170 43 172 47 171 52 L169 107"
            />
            <path
              className="fit-ink fit-draw fit-case-main"
              pathLength="1"
              d="M88 109 C94 98 104 94 118 94 L186 99 C199 100 207 108 208 121 L218 214 C220 229 212 238 198 239 L105 235 C91 234 82 225 84 210 Z"
            />
            <path
              className="fit-ink fit-soft-ink fit-draw"
              pathLength="1"
              d="M187 100 C178 129 180 205 198 238 M104 124 C100 153 99 194 102 217 M130 121 C126 153 126 197 130 220 M156 122 C152 154 153 198 157 221"
            />
            <path
              className="fit-ink fit-draw"
              pathLength="1"
              d="M95 116 C120 106 164 108 197 116"
            />
            <path
              className="fit-ink fit-draw"
              pathLength="1"
              d="M101 235 C97 246 102 253 111 254 C119 254 124 248 121 238 M190 239 C187 249 192 256 201 255 C209 254 213 247 209 239"
            />
            <path
              className="fit-ink fit-soft-ink"
              d="M65 258 C105 254 160 259 241 255"
            />

            <g className="fit-lax-tag">
              <path
                className="fit-ink"
                d="M202 111 C214 104 222 108 228 119"
              />
              <path
                className="fit-ink"
                d="M221 112 L258 133 L244 167 L207 143 Z"
              />
              <circle
                className="fit-ink"
                cx="220"
                cy="124"
                r="3"
              />
              <text x="220" y="146">
                LAX
              </text>
            </g>
          </g>

          <path
            className="fit-travel-line"
            pathLength="1"
            d="M232 191 C271 170 284 210 319 191 C348 175 365 196 394 181"
          />
          <path
            className="fit-travel-arrow"
            pathLength="1"
            d="M386 174 C396 176 401 181 393 190"
          />
        </svg>

        <span className="fit-hand-note fit-la-note">
          JUST LANDED
        </span>
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className="fit-reason-visual">
        <svg
          className="fit-editorial-art fit-id-art"
          viewBox="0 0 420 300"
          role="img"
          aria-label="A person wearing a new identification badge"
        >
          <g className="fit-id-person">
            <path
              className="fit-person-hair"
              d="M145 95 C141 68 158 46 183 44 C210 42 228 61 228 88 C228 111 217 129 202 139 L165 139 C151 127 144 113 145 95 Z"
            />
            <path
              className="fit-person-face-paper fit-draw"
              pathLength="1"
              d="M184 55 C164 55 151 70 151 91 C151 111 165 127 184 130 C203 128 217 112 219 92 C220 71 207 55 184 55 Z"
            />
            <path
              className="fit-ink fit-hairline"
              d="M151 86 C169 83 181 72 189 58 C196 74 207 83 219 87"
            />
            <path
              className="fit-ink fit-face-line"
              d="M169 92 C172 90 176 90 179 92 M197 92 C200 90 204 90 207 92 M190 96 C193 102 193 108 188 111 M179 119 C186 124 195 124 201 118"
            />
            <path
              className="fit-ink fit-draw fit-neck"
              pathLength="1"
              d="M168 127 L166 148 M201 127 L203 148"
            />
            <path
              className="fit-person-shirt-paper fit-draw"
              pathLength="1"
              d="M165 146 C130 152 106 178 99 221 L99 239 C140 249 226 250 270 239 L270 221 C263 179 238 153 203 146 C192 154 177 154 165 146 Z"
            />
            <path
              className="fit-ink fit-soft-ink"
              d="M165 147 L184 169 L203 147 M184 169 L184 222"
            />
            <path
              className="fit-ink fit-soft-ink"
              d="M99 221 C140 232 228 233 270 221"
            />

            <g className="fit-lanyard">
              <path
                className="fit-ink fit-lanyard-loop"
                pathLength="1"
                d="M165 148 C166 177 173 197 184 214 M203 148 C201 177 195 197 184 214"
              />
              <path
                className="fit-ink"
                d="M178 211 L190 211 L192 222 L176 222 Z"
              />
              <g className="fit-id-badge">
                <path
                  className="fit-badge-paper"
                  d="M149 221 C149 216 153 213 158 214 L211 216 C216 216 219 220 218 225 L214 277 L145 274 Z"
                />
                <circle
                  className="fit-ink"
                  cx="168"
                  cy="239"
                  r="9"
                />
                <path
                  className="fit-ink"
                  d="M155 262 C158 249 165 245 172 246 C181 247 186 253 187 264"
                />
                <path
                  className="fit-ink fit-soft-ink"
                  d="M193 237 L207 238 M192 249 L207 250 M191 261 L203 262"
                />
              </g>
            </g>
          </g>

          <path
            className="fit-motion-mark fit-motion-left"
            d="M127 119 C111 130 106 144 109 157"
          />
          <path
            className="fit-motion-mark fit-motion-right"
            d="M239 119 C255 130 260 144 257 157"
          />
        </svg>

        <span className="fit-hand-note fit-id-note">
          NEW ROUTINE
        </span>
      </div>
    );
  }

  if (index === 2) {
    return (
      <div className="fit-reason-visual">
        <svg
          className="fit-editorial-art fit-journal-art"
          viewBox="0 0 420 300"
          role="img"
          aria-label="An open journal turning to a new page"
        >
          <g className="fit-journal-object">
            <path
              className="fit-page-paper"
              d="M61 89 C121 66 171 69 210 88 L210 242 C169 221 119 221 61 238 Z"
            />
            <path
              className="fit-page-paper"
              d="M210 88 C257 65 308 68 363 91 L363 238 C309 221 260 222 210 242 Z"
            />
            <path
              className="fit-ink fit-soft-ink"
              d="M210 91 C215 145 215 199 210 239"
            />
            <path
              className="fit-ink fit-soft-ink"
              d="M236 124 C267 113 306 114 337 124 M236 147 C268 136 306 138 337 147 M237 170 C267 160 305 161 334 170 M237 193 C266 184 301 185 330 193"
            />

            <g className="fit-turning-page">
              <path
                className="fit-page-paper"
                d="M61 89 C121 66 171 69 210 88 L210 242 C169 221 119 221 61 238 Z"
              />
              <path
                className="fit-ink fit-soft-ink"
                d="M87 124 C119 113 159 114 188 124 M87 147 C120 136 159 138 188 147 M88 170 C119 160 157 161 185 170 M89 193 C118 184 154 185 182 193"
              />
            </g>

            <path
              className="fit-fresh-mark"
              pathLength="1"
              d="M258 188 C274 171 294 166 316 171 M287 157 L287 203"
            />
          </g>

          <path
            className="fit-page-air"
            pathLength="1"
            d="M166 56 C211 30 260 39 291 67"
          />
          <path
            className="fit-page-air"
            pathLength="1"
            d="M278 55 C290 57 296 63 289 74"
          />
        </svg>

        <span className="fit-hand-note fit-phase-note">
          FRESH CHAPTER
        </span>
      </div>
    );
  }

  return (
    <div className="fit-reason-visual">
      <svg
        className="fit-editorial-art fit-circle-art"
        viewBox="0 0 420 300"
        role="img"
        aria-label="A fourth chair joining a conversational circle"
      >
        <g className="fit-circle-table">
          <ellipse
            className="fit-table-paper"
            cx="212"
            cy="148"
            rx="81"
            ry="46"
          />
          <path
            className="fit-ink fit-soft-ink"
            d="M133 151 C158 188 267 197 291 151"
          />

          <g className="fit-place-setting fit-place-one">
            <path
              className="fit-ink"
              d="M177 123 C177 115 193 115 193 123 V137 C193 145 177 145 177 137 Z M193 125 C203 123 205 134 194 135"
            />
            <path
              className="fit-ink fit-soft-ink"
              d="M170 148 C180 152 192 152 201 148"
            />
          </g>

          <g className="fit-place-setting fit-place-two">
            <path
              className="fit-ink"
              d="M230 122 C230 114 246 114 246 122 V136 C246 144 230 144 230 136 Z M246 124 C256 122 258 133 247 134"
            />
            <path
              className="fit-ink fit-soft-ink"
              d="M223 147 C233 151 245 151 254 147"
            />
          </g>

          <g className="fit-place-setting fit-place-three">
            <path
              className="fit-ink"
              d="M203 160 C203 152 219 152 219 160 V174 C219 182 203 182 203 174 Z M219 162 C229 160 231 171 220 172"
            />
            <path
              className="fit-ink fit-soft-ink"
              d="M196 185 C206 189 218 189 227 185"
            />
          </g>

          <Chair
            className="fit-chair-one"
            x={87}
            y={93}
          />
          <Chair
            className="fit-chair-two"
            x={276}
            y={91}
          />
          <Chair
            className="fit-chair-three"
            x={182}
            y={26}
          />
          <Chair
            className="fit-chair-four"
            x={183}
            y={210}
          />

          <path
            className="fit-conversation-thread"
            pathLength="1"
            d="M130 105 C153 87 172 83 191 86 M233 85 C256 84 273 91 289 106 M288 189 C267 207 249 214 228 215 M192 216 C169 211 150 201 133 186"
          />
        </g>
      </svg>

      <span className="fit-hand-note fit-circle-note">
        ROOM FOR ONE MORE
      </span>
    </div>
  );
}

function Chair({ className, x, y }) {
  return (
    <g
      transform={`translate(${x} ${y})`}
    >
      <g className={`fit-chair ${className}`}>
        <path
          className="fit-ink fit-draw"
          pathLength="1"
          d="M24 0 C12 -3 3 5 2 18 L0 50 C-1 60 6 67 17 67 H42 C52 67 59 60 58 50 L55 18 C54 6 44 1 24 0 Z"
        />
        <path
          className="fit-ink fit-soft-ink fit-draw"
          pathLength="1"
          d="M10 68 L7 91 M47 68 L51 91"
        />
      </g>
    </g>
  );
}

function ProfileSignals({ stage }) {
  const activeIndex = Math.min(
    Math.max(stage - 1, -1),
    PROFILE_SIGNALS.length - 1
  );
  const activeSignal =
    activeIndex >= 0
      ? PROFILE_SIGNALS[activeIndex]
      : null;

  const zoneClass = (key) => {
    if (!activeSignal) {
      return 'is-open';
    }

    return activeSignal.key === key
      ? 'is-focused'
      : 'is-muted';
  };

  return (
    <section className="who-slide profile-scan-slide">
      <div className="profile-scan-layout">
        <div
          className="profile-scan-copy"
          key={activeSignal?.key || 'intro'}
        >
          {activeSignal ? (
            <>
              <strong>{activeSignal.title}</strong>
              <p>{activeSignal.detail}</p>
            </>
          ) : (
            <strong>
              Read the profile
              <br />
              as a whole
            </strong>
          )}
        </div>

        <article
          className={[
            'scan-profile-card instagram-profile',
            activeSignal?.key === 'life'
              ? 'is-viewing-post'
              : '',
          ].join(' ')}
        >
          <div className="instagram-topbar">
            <button type="button" aria-label="Back">‹</button>
            <strong>maya.moves</strong>
            <span>•••</span>
          </div>

          <div className="instagram-profile-header">
            <div className="instagram-avatar-ring">
              <img
                className="scan-avatar"
                src={INSTAGRAM_PROFILE_PHOTO}
                alt="Maya Young"
              />
            </div>

            <div className="instagram-stats">
              <span><strong>46</strong>posts</span>
              <span><strong>1,284</strong>followers</span>
              <span><strong>812</strong>following</span>
            </div>
          </div>

          <div className="instagram-profile-content">
            <div className="instagram-name-line">
              <strong>Maya Young</strong>
              <span>she/her</span>
            </div>

            <div className="instagram-bio">
              <strong
                className={[
                  'scan-zone instagram-origin-clue',
                  zoneClass('bio'),
                ].join(' ')}
              >
                Philly → LA
              </strong>

              <span
                className={[
                  'scan-zone instagram-age-clue',
                  zoneClass('age'),
                ].join(' ')}
              >
                USC · 🩺
              </span>
            </div>

            <div className="instagram-actions">
              <button type="button">Follow</button>
              <button type="button">Message</button>
              <button
                type="button"
                aria-label="Suggested accounts"
              >
                ⌄
              </button>
            </div>

            <div
              className={[
                'scan-zone instagram-highlights',
                zoneClass('life'),
              ].join(' ')}
            >
              {['FRIENDS', 'LA', 'NIGHTS'].map((item) => (
                <div key={item}>
                  <span><LuHeart aria-hidden="true" /></span>
                  <small>{item}</small>
                </div>
              ))}
            </div>
          </div>

          <div className="instagram-tabs" aria-hidden="true">
            <span className="instagram-grid-icon">
              {Array.from({ length: 9 }).map((_, index) => (
                <i key={index} />
              ))}
            </span>
            <LuUsersRound />
          </div>

          <div
            className={[
              'scan-zone instagram-photo-grid',
              activeSignal?.key === 'age'
                ? 'is-age-scan'
                : zoneClass('photos'),
            ].join(' ')}
          >
            {INSTAGRAM_PHOTOS.map((photo, index) => (
              <div
                className={[
                  'instagram-photo',
                  activeSignal?.key === 'age'
                    ? [1, 5].includes(index)
                      ? 'is-age-focused'
                      : 'is-age-muted'
                    : '',
                ].join(' ')}
                key={photo.src}
              >
                <img src={photo.src} alt={photo.alt} />
              </div>
            ))}
          </div>

          {activeSignal?.key === 'life' && (
            <div className="instagram-post-view">
              <div className="instagram-post-topbar">
                <span>‹</span>
                <strong>Post</strong>
                <i>•••</i>
              </div>

              <div className="instagram-post-user">
                <img
                  src={INSTAGRAM_PROFILE_PHOTO}
                  alt=""
                />
                <strong>maya.moves</strong>
              </div>

              <img
                className="instagram-post-image"
                src={INSTAGRAM_PHOTOS[5].src}
                alt={INSTAGRAM_PHOTOS[5].alt}
              />

              <div className="instagram-post-actions">
                <div>
                  <LuHeart aria-hidden="true" />
                  <LuMessageCircle aria-hidden="true" />
                  <LuSend aria-hidden="true" />
                </div>
                <LuBookmark aria-hidden="true" />
              </div>

              <div className="instagram-post-likes">
                Liked by <strong>livvy.jpg</strong> and{' '}
                <strong>127 others</strong>
              </div>

              <div className="instagram-post-caption">
                <p>
                  <strong>maya.moves</strong>{' '}
                  manifesting my meet-cute era 💌
                </p>
              </div>

              <div className="instagram-post-comments">
                <span>View all 18 comments</span>
                <p>
                  <strong>livvy.jpg</strong>{' '}
                  okayyy this is the energy
                </p>
                <p>
                  <strong>ashleyonline</strong>{' '}
                  your meet cute is loading babe
                </p>
              </div>
            </div>
          )}
        </article>
      </div>
    </section>
  );
}

function ProspectGame({ onComplete }) {
  const [profileIndex, setProfileIndex] =
    useState(0);
  const [answer, setAnswer] =
    useState(null);
  const [score, setScore] = useState(0);
  const [result, setResult] =
    useState(null);

  const profile =
    PROSPECT_PROFILES[profileIndex];
  const answeredCorrectly =
    answer !== null &&
    answer === profile.goodProspect;

  const chooseAnswer = (choice) => {
    if (answer !== null) {
      return;
    }

    setAnswer(choice);

    if (choice === profile.goodProspect) {
      setScore(
        (currentScore) =>
          currentScore + 1
      );
    }
  };

  const continueGame = () => {
    if (
      profileIndex ===
      PROSPECT_PROFILES.length - 1
    ) {
      const passed = score >= 6;
      setResult(
        passed ? 'passed' : 'failed'
      );

      if (passed) {
        onComplete?.();
      }

      return;
    }

    setProfileIndex(
      (currentIndex) => currentIndex + 1
    );
    setAnswer(null);
  };

  const restartGame = () => {
    setProfileIndex(0);
    setAnswer(null);
    setScore(0);
    setResult(null);
  };

  if (result) {
    const passed = result === 'passed';

    return (
      <section className="who-slide prospect-results-slide">
        <div className="prospect-results-count">
          <strong>{score}</strong>
          <span>
            / {PROSPECT_PROFILES.length}
          </span>
        </div>

        <h1>
          {passed
            ? 'You passed.'
            : 'You need 6 out of 8 to pass.'}
        </h1>

        {passed ? (
          <p>
            You correctly identified enough profiles. You can
            continue to the next section.
          </p>
        ) : (
          <>
            <p>
              Review the full profile instead of relying on one
              isolated signal, then try all eight profiles again.
            </p>
            <button
              type="button"
              onClick={restartGame}
            >
              Try again
            </button>
          </>
        )}
      </section>
    );
  }

  return (
    <section className="who-slide prospect-game-slide">
      <header className="prospect-game-heading">
        <span className="prospect-game-eyebrow">CHECK-IN</span>
        <h1>Would you contact {profile.name}?</h1>
        <p>
          Review the full profile and make your call. Get at least six
          of eight right to continue.
        </p>
      </header>

      <div className="prospect-game-body">
        <article
          className="mock-profile"
          key={profile.handle}
        >
          <header className="mock-profile-identity">
            <img
              src={profile.photo}
              alt={`${profile.name} profile`}
            />

            <div>
              <h2>
                {profile.name}, {profile.age}
              </h2>
              <span>
                @{profile.handle} · {profile.location}
              </span>
            </div>
          </header>

          {profile.bio && (
            <p>{profile.bio}</p>
          )}

          <div className="mock-profile-read">
            <div className="mock-profile-snapshot">
              {profile.snapshot.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </article>

        <div className="prospect-decision">
          {answer === null ? (
            <>
              <div className="prospect-choice-row">
                <button
                  type="button"
                  onClick={() =>
                    chooseAnswer(false)
                  }
                >
                  <span>A</span>
                  <strong>Not a fit</strong>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    chooseAnswer(true)
                  }
                >
                  <span>B</span>
                  <strong>Reach out</strong>
                </button>
              </div>
            </>
          ) : (
            <div
              className={[
                'prospect-feedback',
                answeredCorrectly
                  ? 'is-correct'
                  : 'is-incorrect',
              ].join(' ')}
            >
              <div className="prospect-feedback-heading">
                <i aria-hidden="true">
                  {answeredCorrectly ? '✓' : '×'}
                </i>
                <h2>
                  {answeredCorrectly
                    ? 'That’s right.'
                    : 'Not quite.'}
                </h2>
              </div>
              <p>{profile.feedback}</p>
              <button
                type="button"
                onClick={continueGame}
              >
                {profileIndex ===
                PROSPECT_PROFILES.length - 1
                  ? 'See results'
                  : 'Next profile'}
              </button>
            </div>
          )}

          <div
            className="prospect-pagination"
            role="progressbar"
            aria-label="Profile progress"
            aria-valuemin="1"
            aria-valuemax={PROSPECT_PROFILES.length}
            aria-valuenow={profileIndex + 1}
          >
            {PROSPECT_PROFILES.map((item, index) => (
              <span
                className={
                  index === profileIndex
                    ? 'is-current'
                    : ''
                }
                key={item.handle}
                aria-hidden="true"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const STYLES = `
  .who-slide {
    --lesson-hero-size: clamp(42px, 5.4vw, 70px);
    --lesson-hero-weight: 820;
    --lesson-hero-tracking: -.045em;

    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 465px;
    height: 100%;
    padding: clamp(28px, 4.5vh, 48px)
      clamp(20px, 4vw, 52px);
    box-sizing: border-box;
    overflow: hidden;
    background: #ffffff;
    color: #111111;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
    text-rendering: optimizeLegibility;
  }

  .overview-slide {
    position: relative;
    align-items: center;
    justify-content: center;
  }

  .overview-intro-beat {
    width: min(960px, 100%);
    color: #111111;
    font-size: var(--lesson-hero-size);
    font-weight: var(--lesson-hero-weight);
    line-height: 1;
    letter-spacing: var(--lesson-hero-tracking);
    text-align: center;
    animation: overview-intro-enter 480ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .overview-intro-line {
    display: flex;
    align-items: baseline;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0 .22em;
  }

  .overview-intro-handwritten {
    display: inline-block;
    font-family:
      'Ugly Handwriting',
      cursive;
    font-size: 1.08em;
    font-weight: 400;
    line-height: .9;
    letter-spacing: 0;
    word-spacing: -.12em;
    transform: rotate(-1deg);
    transform-origin: center;
  }

  .overview-intro-handwritten .overview-kinetic-words {
    font: inherit;
  }

  .overview-kinetic-words {
    display: inline;
    font: inherit;
  }

  .overview-kinetic-words > span {
    display: inline-block;

    opacity: 0;
    transform: translateY(.28em);

    animation:
      overview-word-reveal
      420ms
      cubic-bezier(.22, 1, .36, 1)
      forwards;

    animation-delay:
      var(--overview-word-delay);
  }

  .overview-step-stack {
    display: grid;
    grid-template-rows: repeat(3, 100px);
    align-content: center;
    width: min(760px, 100%);
    min-height: 300px;
    animation: overview-stack-enter 520ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .overview-step-row {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr);
    align-items: center;
    gap: clamp(18px, 2.5vw, 28px);
    min-height: 100px;
    padding: 9px 0;
    box-sizing: border-box;
  }

  .overview-step-row:not(:last-child)::before,
  .overview-step-row:not(:last-child)::after {
    position: absolute;
    z-index: 0;
    top: calc(50% + 18px);
    left: 9px;
    width: 4px;
    height: 56px;
    border-radius: 999px;
    content: '';
    transform-origin: top center;
  }

  .overview-step-row:not(:last-child)::before {
    background: #d0d0d0;
  }

  .overview-step-row:not(:last-child)::after {
    background: #111111;
    transform: scaleY(0);
  }

  .overview-step-row.is-complete:not(:last-child)::after,
  .overview-step-stack.is-reviewing
  .overview-step-row:not(:last-child)::after {
    transform: scaleY(1);
    transition: transform 650ms
      cubic-bezier(.22, 1, .36, 1);
  }

  .overview-step-row.is-current:not(:last-child)::after {
    animation: overview-segment-progress 1900ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .overview-step-marker {
    position: relative;
    z-index: 2;
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    box-sizing: border-box;
    background: #ffffff;
    color: #ffffff;
    border: 1.5px solid #c9c9c9;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
    transition:
      background 450ms ease,
      border-color 450ms ease,
      transform 600ms cubic-bezier(.22, 1, .36, 1);
  }

  .overview-step-row.is-current .overview-step-marker {
    border-color: #111111;
    transform: scale(1.08);
    animation: none;
  }

  .overview-step-row.is-current .overview-step-marker::before {
    width: 7px;
    height: 7px;
    background: #111111;
    border-radius: 50%;
    content: '';
  }

  .overview-step-row.is-complete .overview-step-marker {
    background: #111111;
    border-color: #111111;
  }

  .overview-step-copy {
    display: grid;
    gap: 8px;
    opacity: 0;
    transform: translateY(14px);
    transform-origin: left center;
    transition:
      opacity 450ms ease,
      transform 650ms cubic-bezier(.22, 1, .36, 1);
  }

  .overview-step-row.is-visible .overview-step-copy {
    opacity: 1;
    transform: translateY(0);
  }

  .overview-step-copy strong {
    font-size: clamp(24px, 2.8vw, 35px);
    font-weight: 720;
    line-height: 1;
    letter-spacing: -.035em;
    transition:
      color 450ms ease,
      font-size 700ms cubic-bezier(.22, 1, .36, 1),
      font-weight 450ms ease;
  }

  .overview-step-action {
    display: inline-block;
    margin-right: .03em;
    font-family:
      'Ugly Handwriting',
      cursive;
    font-size: 1.2em;
    font-weight: 400;
    line-height: .9;
    letter-spacing: 0;
    transform: translateY(.025em) rotate(-1deg);
    transform-origin: center;
  }

  .overview-step-row.is-current {
    min-height: 100px;
  }

  .overview-step-row.is-current .overview-step-copy strong {
    color: #111111;
    font-size: clamp(39px, 5vw, 60px);
    font-weight: 820;
    letter-spacing: -.045em;
  }

  .overview-step-row.is-visible.is-previous .overview-step-copy {
    color: #111111;
    opacity: 1;
  }

  .overview-step-row.is-visible.is-previous .overview-step-copy strong {
    font-size: clamp(24px, 2.8vw, 35px);
    font-weight: 700;
  }

  .overview-step-stack.is-reviewing {
    grid-template-rows: repeat(3, 100px);
    min-height: 300px;
  }

  .overview-step-stack.is-reviewing .overview-step-row,
  .overview-step-stack.is-reviewing .overview-step-copy {
    opacity: 1;
    transform: translateY(0);
  }

  .overview-step-stack.is-reviewing .overview-step-marker {
    background: #111111;
    color: #ffffff;
    border-color: #111111;
    animation: none;
  }

  .overview-step-stack.is-reviewing .overview-step-copy strong {
    color: #111111;
    font-size: clamp(28px, 3.3vw, 40px);
    font-weight: 760;
    letter-spacing: -.035em;
  }

  .target-slide {
    padding: 18px clamp(20px, 4vw, 52px);
  }

  .target-scene-stage {
    position: relative;
    width: min(820px, 100%);
    height: 390px;
  }

  .target-intro,
  .target-review {
    position: absolute;
    inset: 0;
    z-index: 4;
    opacity: 0;
    pointer-events: none;
  }

  .target-intro {
    display: grid;
    place-items: center;
    transition: opacity 300ms ease;
  }

  .target-intro.is-visible,
  .target-review.is-visible {
    opacity: 1;
  }

  .target-intro h1 {
    max-width: 860px;
    margin: 0;
    font-size: var(--lesson-hero-size);
    font-weight: var(--lesson-hero-weight);
    line-height: 1;
    letter-spacing: var(--lesson-hero-tracking);
    text-align: center;
  }

  .target-intro-word {
    display: inline-block;
    opacity: 0;
    transform: translateY(.35em);
  }

  .target-intro-hand-group {
    display: inline;
    font-family: 'Ugly Handwriting', cursive;
    font-size: 1.08em;
    font-weight: 400;
    line-height: .9;
    letter-spacing: 0;
    word-spacing: -.14em;
  }

  .target-intro-hand-group .target-intro-word {
    font-family: 'Ugly Handwriting', cursive;
    font-weight: 400;
    letter-spacing: 0;
  }

  .target-intro.is-visible
  .target-intro-word {
    animation: target-intro-word-in 420ms
      cubic-bezier(.22, 1, .36, 1) forwards;
    animation-delay: var(--target-word-delay);
  }

  .target-review {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transform: translateY(18px);
    transition:
      opacity 520ms ease,
      transform 700ms cubic-bezier(.22, 1, .36, 1);
  }

  .target-review.is-visible {
    transform: translateY(0);
  }

  .target-review h2 {
    width: 100%;
    margin: 0 0 24px;
    color: #5f5f5f;
    font-size: clamp(14px, 1.6vw, 18px);
    font-weight: 760;
    letter-spacing: .045em;
    text-align: center;
    opacity: 0;
  }

  .target-review.is-visible h2 {
    animation: target-review-heading-in 420ms 100ms
      ease forwards;
  }

  .target-review-quadrants {
    position: relative;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: min(660px, 100%);
  }

  .target-review-quadrants::before,
  .target-review-quadrants::after {
    position: absolute;
    z-index: 2;
    background: #e4e4e4;
    content: '';
    transform-origin: center;
  }

  .target-review-quadrants::before {
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    transform: scaleY(0);
  }

  .target-review-quadrants::after {
    top: 50%;
    right: 0;
    left: 0;
    height: 1px;
    transform: scaleX(0);
  }

  .target-review.is-visible
  .target-review-quadrants::before {
    animation: review-divider-vertical 620ms 140ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .target-review.is-visible
  .target-review-quadrants::after {
    animation: review-divider-horizontal 620ms 240ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .target-review-quadrant {
    display: grid;
    align-content: center;
    justify-items: start;
    min-height: 128px;
    padding: 25px clamp(26px, 4vw, 46px);
    box-sizing: border-box;
    opacity: 0;
    transform: translateY(12px);
  }

  .target-review-quadrant > svg {
    width: 19px;
    height: 19px;
    margin-bottom: 16px;
    color: #737373;
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .target-review-quadrant
  .review-female-icon {
    fill: none;
    stroke: currentColor;
    stroke-width: 24;
  }

  .target-review-quadrant > strong {
    color: #202020;
    font-size: clamp(17px, 2.1vw, 25px);
    font-weight: 790;
    line-height: 1;
    letter-spacing: -.045em;
  }

  .target-review-quadrant > small {
    margin-top: 8px;
    color: #8a8a8a;
    font-size: 8px;
    font-weight: 650;
    letter-spacing: .06em;
  }

  .target-review.is-visible
  .target-review-quadrant {
    animation: target-review-item-in 560ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .target-review.is-visible
  .target-review-quadrant:nth-child(1) {
    animation-delay: 260ms;
  }

  .target-review.is-visible
  .target-review-quadrant:nth-child(2) {
    animation-delay: 360ms;
  }

  .target-review.is-visible
  .target-review-quadrant:nth-child(3) {
    animation-delay: 460ms;
  }

  .target-review.is-visible
  .target-review-quadrant:nth-child(4) {
    animation-delay: 560ms;
  }

  .target-scene {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    pointer-events: none;
    transform: translateY(28px) scale(.985);
    transition:
      opacity 480ms ease,
      transform 680ms cubic-bezier(.22, 1, .36, 1);
  }

  .target-scene.is-current {
    z-index: 2;
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .target-scene.is-before {
    transform: translateY(-28px) scale(.985);
  }

  .la-map {
    width: clamp(100px, 12vw, 138px);
  }

  .la-map svg {
    display: block;
    width: 100%;
    overflow: visible;
  }

  .california-shape {
    transform: rotate(-11deg);
    transform-box: fill-box;
    transform-origin: center;
  }

  .california-guide {
    fill: none;
    stroke: none;
  }

  .california-outline {
    fill: none;
    stroke: #111111;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
    vector-effect: non-scaling-stroke;
  }

  .california-trace-head {
    opacity: 0;
  }

  .trace-head-dot {
    fill: #111111;
  }

  .trace-head-pulse {
    fill: rgba(17, 17, 17, .16);
    animation: trace-head-pulse 1.15s ease-out infinite;
    transform-box: fill-box;
    transform-origin: center;
  }

  .los-angeles-marker {
    opacity: 0;
    transform: scale(.25);
    transform-box: fill-box;
    transform-origin: center;
  }

  .la-dot {
    fill: #ff675f;
  }

  .la-pulse {
    fill: rgba(255, 103, 95, .3);
  }

  .los-angeles-marker.is-visible {
    animation: la-marker-in 520ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .los-angeles-marker.is-visible .la-pulse {
    animation: la-pulse 1.8s .4s ease-out infinite;
    transform-box: fill-box;
    transform-origin: center;
  }

  .la-handwritten-callout {
    opacity: 0;
  }

  .la-handwritten-callout.is-visible {
    opacity: 1;
  }

  .la-callout-arrow {
    fill: none;
    stroke: #111111;
    stroke-width: 1.35;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    vector-effect: non-scaling-stroke;
  }

  .la-callout-text {
    fill: #111111;
    font-family: 'Ugly Handwriting', cursive;
    font-size: 28px;
    font-weight: 500;
    letter-spacing: 0;
    opacity: 0;
  }

  .la-handwritten-callout.is-visible
  .la-callout-curve {
    animation: la-callout-draw 560ms 160ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .la-handwritten-callout.is-visible
  .la-callout-head {
    animation: la-callout-draw 240ms 620ms
      ease-out forwards;
  }

  .la-handwritten-callout.is-visible
  .la-callout-text {
    animation: la-callout-text-in 420ms 760ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .age-visual {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: clamp(280px, 39vw, 410px);
  }

  .age-range-copy {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: clamp(14px, 2.4vw, 26px);
  }

  .age-range-copy strong {
    font-family:
      'Ugly Handwriting',
      cursive;
    font-size: clamp(52px, 6.2vw, 74px);
    font-weight: 400;
    line-height: .86;
    letter-spacing: 0;
    transform: rotate(-1deg);
    transform-origin: center;
  }

  .age-range-dash {
    display: block;
    width: 18px;
    height: 2px;
    flex: 0 0 18px;
    background: #111111;
  }

  .age-scale {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 15px;
    margin: 20px 0 9px;
  }

  .age-scale::before {
    position: absolute;
    top: 6px;
    right: 0;
    left: 0;
    height: 2px;
    background: #111111;
    content: '';
    transform: scaleX(0);
    transform-origin: left center;
  }

  .age-scale i {
    position: relative;
    z-index: 1;
    width: 7px;
    height: 7px;
    background: #111111;
    border: 2px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 0 0 1.5px #111111;
    opacity: 0;
    transform: scale(.35);
  }

  .age-visual > span {
    color: #777777;
    font-size: 10px;
    font-weight: 650;
    letter-spacing: .02em;
  }

  .age-scene.is-current .age-range-copy {
    animation: age-numbers-in 580ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .age-scene.is-current .age-scale::before {
    animation: age-line-in 720ms 320ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .age-scene.is-current .age-scale i {
    animation: age-dot-in 360ms 720ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .age-scene.is-current .age-scale i:nth-child(2) {
    animation-delay: 790ms;
  }

  .age-scene.is-current .age-scale i:nth-child(3) {
    animation-delay: 860ms;
  }

  .age-scene.is-current .age-scale i:nth-child(4) {
    animation-delay: 930ms;
  }

  .age-scene.is-current .age-scale i:nth-child(5) {
    animation-delay: 1000ms;
  }

  .audience-ratio {
    display: grid;
    gap: 22px;
    width: clamp(300px, 42vw, 450px);
  }

  .ratio-row {
    display: grid;
    grid-template-columns: 24px 1fr 48px 50px;
    align-items: center;
    gap: 12px;
    opacity: 0;
    transform: translateX(-12px);
  }

  .ratio-row > svg {
    width: 23px;
    height: 23px;
  }

  .ratio-track {
    position: relative;
    height: 2px;
    overflow: hidden;
    background: #e1e1e1;
  }

  .ratio-track i {
    position: absolute;
    inset: 0 auto 0 0;
    width: 0;
    background: #111111;
  }

  .female-stat .ratio-track i {
    --ratio-width: 70%;
  }

  .male-stat .ratio-track i {
    --ratio-width: 30%;
  }

  .ratio-row > strong {
    font-family:
      'Ugly Handwriting',
      cursive;
    font-size: clamp(27px, 3vw, 36px);
    font-weight: 400;
    line-height: .9;
    letter-spacing: 0;
    transform: rotate(-1deg);
    transform-origin: center;
  }

  .ratio-row > small {
    font-size: 9px;
    font-weight: 650;
    letter-spacing: .02em;
  }

  .audience-scene.is-current .female-stat {
    animation: ratio-row-in 520ms 140ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .audience-scene.is-current .male-stat {
    animation: ratio-row-in 520ms 580ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .audience-scene.is-current .ratio-track i {
    animation: ratio-line-in 780ms 420ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .audience-scene.is-current .male-stat .ratio-track i {
    animation-delay: 860ms;
  }

  .life-stage-type {
    display: grid;
    gap: 12px;
    width: min(620px, 100%);
    justify-items: center;
    text-align: center;
  }

  .life-stage-statement {
    position: relative;
    z-index: 1;
    width: max-content;
    max-width: 100%;
    padding: 0 .08em;
    color: #111111;
    font-size: clamp(32px, 4.4vw, 54px);
    font-weight: 780;
    line-height: 1;
    letter-spacing: -.045em;
    text-align: center;
    opacity: 0;
    transform: translateY(18px);
  }

  .life-stage-statement::before,
  .life-stage-statement::after {
    position: absolute;
    z-index: -1;
    right: -.05em;
    left: -.05em;
    content: '';
    transform-origin: left center;
    mix-blend-mode: multiply;
  }

  .life-stage-statement::before {
    bottom: -.01em;
    height: .6em;
    background: rgba(226, 251, 66, .56);
    clip-path: polygon(
      0 18%, 12% 8%, 29% 13%, 48% 5%,
      66% 12%, 84% 6%, 100% 16%,
      99% 87%, 78% 94%, 56% 88%,
      35% 97%, 15% 89%, 1% 95%
    );
    transform: rotate(-.7deg) scaleX(0);
  }

  .life-stage-statement::after {
    bottom: .05em;
    height: .42em;
    background: rgba(239, 255, 103, .24);
    clip-path: polygon(
      0 12%, 19% 4%, 38% 10%, 59% 2%,
      79% 9%, 100% 3%, 99% 91%,
      73% 96%, 49% 88%, 25% 98%, 1% 89%
    );
    transform: rotate(.35deg) scaleX(0);
  }

  .life-stage-scene.is-current
  .life-stage-statement {
    animation: life-stage-statement-in 620ms
      var(--life-stage-delay, 0ms)
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .life-stage-scene.is-current
  .life-stage-statement::before {
    animation: life-stage-highlight-in 680ms
      calc(var(--life-stage-delay, 0ms) + 300ms)
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .life-stage-scene.is-current
  .life-stage-statement::after {
    animation: life-stage-highlight-soft-in 760ms
      calc(var(--life-stage-delay, 0ms) + 410ms)
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .fit-slide {
    text-align: center;
  }

  .fit-kinetic-sentence {
    position: relative;
    display: grid;
    place-items: center;
    width: min(1040px, 94%);
    min-height: 230px;
    margin: 0 auto;
    color: #111111;
    font-size: clamp(42px, 5.4vw, 70px);
    font-weight: 820;
    line-height: 1;
    letter-spacing: -.045em;
    text-align: center;
  }

  .fit-caption-beat {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    align-content: center;
    justify-content: center;
    flex-wrap: wrap;
    column-gap: .22em;
    padding: 0 20px;
    opacity: 0;
    animation: fit-caption-window
      var(--fit-caption-duration)
      cubic-bezier(.22, 1, .36, 1) both;
    animation-delay: var(--fit-caption-delay);
  }

  .fit-caption-beat > span {
    display: inline-block;
    opacity: 0;
    filter: blur(2px);
    transform: translateY(.3em);
    animation: fit-word-reveal 360ms
      cubic-bezier(.22, 1, .36, 1) forwards;
    animation-delay: var(--fit-word-delay);
  }

  .fit-caption-beat > span.fit-starting-point-hand {
    font-family:
      'Ugly Handwriting',
      cursive;
    font-size: 1.16em;
    font-weight: 400;
    line-height: .9;
    letter-spacing: 0;
  }

  .fit-kinetic-intro {
    position: relative;
    display: grid;
    place-items: center;
    width: min(780px, 100%);
    min-height: 270px;
  }

  .fit-kinetic-filters {
    position: absolute;
    top: 78px;
    left: 50%;
    display: grid;
    grid-template-columns: repeat(4, auto);
    gap: clamp(24px, 5vw, 62px);
    color: #858585;
    transform: translateX(-50%);
    transition:
      top 720ms cubic-bezier(.22, 1, .36, 1),
      opacity 420ms ease,
      transform 720ms cubic-bezier(.22, 1, .36, 1);
  }

  .fit-kinetic-filters span {
    font-size: 9px;
    font-weight: 760;
    letter-spacing: .085em;
    opacity: 0;
    transform: translateY(8px);
    animation: threshold-filter-in 420ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .fit-kinetic-filters span:nth-child(2) {
    animation-delay: 90ms;
  }

  .fit-kinetic-filters span:nth-child(3) {
    animation-delay: 180ms;
  }

  .fit-kinetic-filters span:nth-child(4) {
    animation-delay: 270ms;
  }

  .fit-starting-copy {
    position: absolute;
    top: 112px;
    left: 50%;
    display: grid;
    justify-items: center;
    width: 100%;
    transform: translateX(-50%);
    transition:
      opacity 480ms ease,
      transform 680ms cubic-bezier(.22, 1, .36, 1);
  }

  .fit-starting-copy > i {
    width: 0;
    height: 1px;
    margin-top: 17px;
    background: #d3d3d3;
    animation: threshold-line-in 720ms 300ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .fit-starting-copy > strong {
    margin-top: 19px;
    font-size: clamp(34px, 4.7vw, 56px);
    font-weight: 820;
    line-height: .94;
    letter-spacing: -.055em;
    opacity: 0;
    animation: threshold-title-in 620ms 520ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .fit-connect-copy {
    position: absolute;
    top: 50%;
    left: 50%;
    display: grid;
    justify-items: center;
    width: min(650px, 100%);
    text-align: center;
    opacity: 0;
    transform: translate(-50%, -42%) scale(.94);
    transition:
      opacity 520ms 260ms ease,
      transform 720ms 220ms
        cubic-bezier(.22, 1, .36, 1);
  }

  .fit-connect-copy > strong {
    font-size: clamp(44px, 6.6vw, 78px);
    font-weight: 870;
    line-height: .88;
    letter-spacing: -.07em;
  }

  .fit-connect-copy > span {
    margin-top: 18px;
    color: #777777;
    font-size: 11px;
    line-height: 1.4;
  }

  .fit-kinetic-intro.show-connect
  .fit-kinetic-filters {
    top: 30px;
    opacity: .34;
    transform: translateX(-50%) scale(.82);
  }

  .fit-kinetic-intro.show-connect
  .fit-starting-copy {
    opacity: 0;
    transform: translate(-50%, -18px) scale(.94);
  }

  .fit-kinetic-intro.show-connect
  .fit-connect-copy {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  .fit-reason-stage {
    position: relative;
    width: 100%;
    max-width: 1040px;
    min-height: 330px;
    margin: 0 auto;
    text-align: left;
  }

  .fit-reason-card {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(860px, calc(100% - 24px));
    opacity: 0;
    transform: translate(-50%, -50%);
    transition:
      left 760ms cubic-bezier(.22, 1, .36, 1),
      top 760ms cubic-bezier(.22, 1, .36, 1),
      width 760ms cubic-bezier(.22, 1, .36, 1),
      opacity 360ms ease;
  }

  .fit-reason-card-inner {
    display: grid;
    grid-template-columns: minmax(220px, .78fr)
      minmax(360px, 1.22fr);
    align-items: center;
    gap: clamp(30px, 5vw, 64px);
    transform: scale(1);
    transform-origin: center;
    transition: transform 760ms
      cubic-bezier(.22, 1, .36, 1);
  }

  .fit-reason-card.is-active {
    z-index: 2;
    opacity: 1;
  }

  .fit-reason-stage.has-history
  .fit-reason-card.is-active {
    left: 64%;
    width: min(730px, calc(100% - 300px));
  }

  .fit-reason-card.is-complete {
    top: calc(50% + var(--fit-history-y));
    left: 82px;
    z-index: 1;
    opacity: .48;
  }

  .fit-reason-card.is-complete
  .fit-reason-card-inner {
    transform: scale(.18);
  }

  .fit-reason-card.is-complete
  .fit-reason-copy p {
    opacity: 0;
    animation: none;
  }

  .fit-reason-card.is-upcoming {
    left: 68%;
    opacity: 0;
  }

  .fit-final-summary {
    display: grid;
    grid-template-columns: 280px 1px minmax(0, 1fr);
    align-items: center;
    gap: clamp(34px, 5vw, 70px);
    width: min(1040px, 94%);
    margin: 0 auto;
  }

  .fit-final-list {
    display: grid;
    gap: 16px;
    text-align: left;
    opacity: 0;
    transform: translateY(10px);
    animation: fit-final-block-in 420ms ease forwards;
  }

  .fit-final-list-item {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    align-items: center;
    gap: 13px;
    min-height: 34px;
    opacity: .3;
    transition:
      opacity 320ms ease,
      transform 420ms cubic-bezier(.22, 1, .36, 1);
  }

  .fit-final-list-item svg {
    width: 22px;
    height: 22px;
    color: #777777;
    stroke-width: 1.6;
    transition: color 320ms ease;
  }

  .fit-final-list-item strong {
    color: #777777;
    font-size: clamp(12px, 1.2vw, 15px);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -.025em;
    transition:
      color 320ms ease,
      font-weight 320ms ease;
  }

  .fit-final-list-item.is-active {
    opacity: 1;
    transform: translateX(4px);
  }

  .fit-final-list-item.is-active svg,
  .fit-final-list-item.is-active strong {
    color: #111111;
  }

  .fit-final-list-item.is-active strong {
    font-weight: 900;
  }

  .fit-final-list-item.is-review {
    opacity: 1;
    transform: translateX(0);
  }

  .fit-final-list-item.is-review svg,
  .fit-final-list-item.is-review strong {
    color: #333333;
  }

  .fit-final-divider {
    display: block;
    width: 1px;
    height: 190px;
    background: #dddddd;
  }

  .fit-final-message {
    max-width: 680px;
    color: #111111;
    font-size: clamp(38px, 5vw, 64px);
    font-weight: 820;
    line-height: 1;
    letter-spacing: -.045em;
    text-align: left;
    opacity: 0;
    transform: translateY(10px);
    animation: fit-final-block-in 420ms 140ms ease forwards;
  }

  .fit-final-message > span {
    display: inline-block;
    opacity: 0;
    transform: translateY(.28em);
    animation: fit-summary-word-in 340ms
      cubic-bezier(.22, 1, .36, 1) forwards;
    animation-delay: var(--fit-summary-word-delay);
  }

  .fit-final-message > span.fit-summary-handwritten {
    font-family:
      'Ugly Handwriting',
      cursive;
    font-size: 1.16em;
    font-weight: 400;
    line-height: .9;
    letter-spacing: 0;
  }

  .fit-reason-editorial {
    display: grid;
    width: 100%;
    max-width: 860px;
    text-align: left;
    animation: fit-reason-scene-in 520ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .fit-reason-focus {
    display: grid;
    grid-template-columns: minmax(220px, .78fr)
      minmax(360px, 1.22fr);
    align-items: center;
    gap: clamp(30px, 5vw, 64px);
    margin-top: 0;
  }

  .fit-reason-copy {
    display: grid;
    justify-items: start;
  }

  .fit-reason-copy strong {
    max-width: 310px;
    margin-top: 0;
    font-size: clamp(31px, 4.3vw, 50px);
    font-weight: 850;
    line-height: .92;
    letter-spacing: -.06em;
    opacity: 0;
    clip-path: inset(0 100% 0 0);
    animation: reason-title-in 680ms 180ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .fit-reason-copy p {
    max-width: 290px;
    margin: 14px 0 0;
    color: #707070;
    font-size: 11px;
    line-height: 1.45;
    opacity: 0;
    animation: reason-copy-in 420ms 540ms ease forwards;
  }

  .fit-reason-visual {
    position: relative;
    width: 100%;
    min-height: 286px;
    opacity: 0;
    animation: fit-visual-in 560ms 110ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .fit-editorial-art {
    display: block;
    width: 100%;
    height: 286px;
    overflow: visible;
  }

  .fit-ink,
  .fit-travel-line,
  .fit-travel-arrow,
  .fit-motion-mark,
  .fit-page-air,
  .fit-fresh-mark,
  .fit-conversation-thread {
    fill: none;
    stroke: #111111;
    stroke-width: 1.55;
    stroke-linecap: round;
    stroke-linejoin: round;
    vector-effect: non-scaling-stroke;
  }

  .fit-soft-ink {
    stroke: #898989;
    stroke-width: 1;
    opacity: .72;
  }

  .fit-draw,
  .fit-travel-line,
  .fit-travel-arrow,
  .fit-lanyard-loop,
  .fit-page-air,
  .fit-fresh-mark,
  .fit-conversation-thread {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
  }

  .fit-person-hair {
    fill: #111111;
    stroke: none;
  }

  .fit-person-face-paper,
  .fit-person-shirt-paper {
    fill: #ffffff;
    stroke: #111111;
    stroke-width: 1.55;
    stroke-linecap: round;
    stroke-linejoin: round;
    vector-effect: non-scaling-stroke;
  }

  .fit-page-paper,
  .fit-badge-paper,
  .fit-table-paper {
    fill: #ffffff;
    stroke: #111111;
    stroke-width: 1.55;
    stroke-linecap: round;
    stroke-linejoin: round;
    vector-effect: non-scaling-stroke;
  }

  .fit-hand-note {
    position: absolute;
    display: block;
    color: #727272;
    font-family:
      'Ugly Handwriting',
      'Rock Salt',
      cursive;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: .01em;
    opacity: 0;
    transform: translateY(7px) rotate(-3deg);
    animation: fit-note-in 460ms 1260ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .fit-la-note {
    right: 18px;
    bottom: 25px;
  }

  .fit-id-note {
    right: 34px;
    bottom: 32px;
  }

  .fit-phase-note {
    right: 26px;
    bottom: 27px;
  }

  .fit-circle-note {
    right: 8px;
    bottom: 20px;
  }

  .fit-lax-tag {
    transform-origin: 220px 124px;
  }

  .fit-lax-tag text {
    fill: #111111;
    font-family:
      'Ugly Handwriting',
      'Rock Salt',
      cursive;
    font-size: 13px;
  }

  .fit-travel-line,
  .fit-travel-arrow,
  .fit-motion-mark,
  .fit-page-air,
  .fit-conversation-thread {
    stroke: #777777;
    stroke-width: 1;
  }

  .fit-lanyard {
    transform-origin: 183px 148px;
  }

  .fit-id-badge {
    transform-origin: 183px 216px;
  }

  .fit-motion-mark {
    opacity: 0;
  }

  .fit-turning-page {
    transform-box: fill-box;
    transform-origin: right center;
  }

  .fit-fresh-mark {
    stroke-width: 1.25;
  }

  .fit-circle-table {
    transform-origin: 212px 151px;
  }

  .fit-chair-four {
    transform-origin: 29px 45px;
  }

  .fit-arrival-art .fit-suitcase-object {
    animation: fit-suitcase-arrive 820ms 80ms
      cubic-bezier(.18, .9, .2, 1) both;
  }

  .fit-arrival-art .fit-draw {
    animation: fit-ink-draw 920ms 180ms ease forwards;
  }

  .fit-arrival-art .fit-lax-tag {
    animation: fit-tag-sway 1180ms 540ms
      cubic-bezier(.36, .07, .19, .97) both;
  }

  .fit-arrival-art .fit-travel-line {
    animation: fit-ink-draw 860ms 680ms ease forwards;
  }

  .fit-arrival-art .fit-travel-arrow {
    animation: fit-ink-draw 320ms 1450ms ease forwards;
  }

  .fit-id-art .fit-id-person {
    animation: fit-person-in 610ms 80ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .fit-id-art .fit-draw {
    animation: fit-ink-draw 820ms 120ms ease forwards;
  }

  .fit-id-art .fit-lanyard-loop {
    animation: fit-ink-draw 680ms 470ms ease forwards;
  }

  .fit-id-art .fit-lanyard {
    animation: fit-lanyard-drop 940ms 420ms
      cubic-bezier(.22, .76, .25, 1) both;
  }

  .fit-id-art .fit-id-badge {
    animation: fit-badge-sway 1180ms 690ms
      cubic-bezier(.36, .07, .19, .97) both;
  }

  .fit-id-art .fit-motion-mark {
    animation: fit-motion-in 560ms 760ms ease both;
  }

  .fit-journal-art .fit-journal-object {
    animation: fit-journal-in 620ms 80ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .fit-journal-art .fit-turning-page {
    animation: fit-page-turn 1420ms 390ms
      cubic-bezier(.42, .05, .22, 1) both;
  }

  .fit-journal-art .fit-page-air {
    animation: fit-ink-draw 580ms 580ms ease forwards;
  }

  .fit-journal-art .fit-fresh-mark {
    animation: fit-ink-draw 560ms 1400ms ease forwards;
  }

  .fit-circle-art .fit-circle-table {
    animation: fit-table-in 500ms 80ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .fit-circle-art .fit-chair-one,
  .fit-circle-art .fit-chair-two,
  .fit-circle-art .fit-chair-three {
    animation: fit-chair-in 500ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .fit-circle-art .fit-chair-one {
    animation-delay: 260ms;
  }

  .fit-circle-art .fit-chair-two {
    animation-delay: 440ms;
  }

  .fit-circle-art .fit-chair-three {
    animation-delay: 620ms;
  }

  .fit-circle-art .fit-chair-four {
    animation: fit-fourth-chair-in 760ms 800ms
      cubic-bezier(.18, .9, .2, 1) both;
  }

  .fit-circle-art .fit-draw {
    animation: fit-ink-draw 640ms ease forwards;
  }

  .fit-circle-art .fit-conversation-thread {
    animation: fit-ink-draw 720ms 1250ms ease forwards;
  }

  .fit-final-refined {
    display: grid;
    justify-items: center;
    width: min(760px, 100%);
  }

  .fit-final-refined > strong {
    font-size: clamp(40px, 6vw, 70px);
    font-weight: 850;
    line-height: .9;
    letter-spacing: -.065em;
    opacity: 0;
    animation: refined-final-title 680ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .fit-final-criteria {
    display: grid;
    grid-template-columns: repeat(3, auto);
    gap: clamp(24px, 5vw, 56px);
    margin-top: 34px;
    padding-top: 22px;
    border-top: 1px solid #dddddd;
  }

  .fit-final-criteria span {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #555555;
    font-size: 9px;
    font-weight: 760;
    letter-spacing: .065em;
    opacity: 0;
    transform: translateY(10px);
    animation: refined-criterion-in 480ms 420ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .fit-final-criteria span:nth-child(2) {
    animation-delay: 540ms;
  }

  .fit-final-criteria span:nth-child(3) {
    animation-delay: 660ms;
  }

  .fit-final-criteria svg {
    width: 15px;
    height: 15px;
    stroke-width: 1.7;
  }

  .profile-scan-layout {
    display: grid;
    grid-template-columns: minmax(260px, .9fr)
      minmax(320px, 1.1fr);
    align-items: center;
    gap: clamp(34px, 7vw, 86px);
    width: min(900px, 100%);
  }

  .profile-scan-copy {
    text-align: left;
    animation: profile-copy-in 620ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .profile-scan-copy strong {
    display: block;
    max-width: 430px;
    font-size: clamp(29px, 4vw, 50px);
    font-weight: 820;
    line-height: .96;
    letter-spacing: -.045em;
  }

  .profile-scan-copy p {
    max-width: 360px;
    margin: 18px 0 0;
    color: #626262;
    font-size: 13px;
    line-height: 1.5;
  }

  .scan-profile-card {
    position: relative;
    width: min(300px, 100%);
    height: 580px;
    box-sizing: border-box;
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 0;
    box-shadow: 0 22px 65px rgba(0, 0, 0, .11);
    overflow: hidden;
  }

  .instagram-topbar {
    display: grid;
    grid-template-columns: 37px 1fr 37px;
    align-items: center;
    height: 48px;
    padding: 0 12px;
    box-sizing: border-box;
    border-bottom: 1px solid #efefef;
  }

  .instagram-topbar button {
    width: 37px;
    padding: 0;
    background: transparent;
    border: 0;
    color: #111111;
    font-size: 29px;
    line-height: 1;
    text-align: left;
  }

  .instagram-topbar strong {
    font-size: 13px;
    font-weight: 750;
    text-align: center;
  }

  .instagram-topbar > span {
    font-size: 11px;
    letter-spacing: 2px;
    text-align: right;
  }

  .instagram-profile-header {
    display: grid;
    grid-template-columns: 100px minmax(0, 1fr);
    align-items: center;
    gap: 0;
    padding: 17px 16px 10px;
  }

  .instagram-avatar-ring {
    display: grid;
    place-items: center;
    width: 77px;
    height: 77px;
    padding: 2px;
    box-sizing: border-box;
    background: linear-gradient(
      135deg,
      #feda75,
      #fa7e1e 28%,
      #d62976 52%,
      #962fbf 76%,
      #4f5bd5
    );
    border-radius: 50%;
  }

  .scan-avatar {
    display: block;
    width: 71px;
    height: 71px;
    border: 2px solid #ffffff;
    border-radius: 50%;
    object-fit: cover;
  }

  .instagram-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2px;
    text-align: center;
  }

  .instagram-stats span {
    color: #555555;
    font-size: 8px;
    line-height: 1.15;
  }

  .instagram-stats strong {
    display: block;
    margin-bottom: 2px;
    color: #111111;
    font-size: 12px;
    font-weight: 780;
  }

  .instagram-profile-content {
    padding: 0 17px 10px;
  }

  .instagram-name-line {
    display: flex;
    align-items: center;
    gap: 4px;
    width: max-content;
    margin-left: 0;
    padding: 0 0 4px;
  }

  .instagram-name-line strong {
    font-size: 11px;
    font-weight: 780;
  }

  .instagram-name-line span {
    color: #737373;
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0;
  }

  .scan-zone {
    position: relative;
    box-sizing: border-box;
    border: 1px solid transparent;
    border-radius: 10px;
    transition:
      opacity 420ms ease,
      filter 420ms ease,
      background 420ms ease,
      transform 520ms cubic-bezier(.22, 1, .36, 1);
  }

  .scan-zone.is-muted {
    opacity: .22;
    filter: blur(1.6px);
  }

  .scan-zone.is-focused {
    z-index: 2;
    background: #ffffff;
    box-shadow: 0 0 0 2px #0095f6;
    filter: blur(0);
    transform: scale(1.015);
  }

  .scan-zone.is-focused::after {
    content: none;
  }

  .instagram-bio {
    display: grid;
    gap: 2px;
    margin-top: 1px;
    padding: 4px 0;
    margin-left: 0;
    font-size: 9px;
    line-height: 1.35;
  }

  .instagram-bio strong {
    display: block;
    width: max-content;
    margin-left: -6px;
    padding: 4px 6px;
    font-size: 9px;
    font-weight: 730;
  }

  .instagram-bio span {
    display: block;
    width: max-content;
    margin-left: -6px;
    padding: 4px 6px;
    color: #3f3f3f;
    font-size: 9px;
  }

  .instagram-actions {
    display: grid;
    grid-template-columns: 1fr 1fr 34px;
    gap: 6px;
    margin-top: 9px;
  }

  .instagram-actions button {
    height: 29px;
    padding: 0;
    background: #efefef;
    border: 0;
    border-radius: 7px;
    color: #111111;
    font-size: 9px;
    font-weight: 730;
  }

  .instagram-actions button:first-child {
    background: #0095f6;
    color: #ffffff;
  }

  .instagram-highlights {
    display: flex;
    gap: 18px;
    margin-top: 10px;
    padding: 7px;
    margin-left: -7px;
    width: max-content;
  }

  .instagram-highlights > div {
    display: grid;
    justify-items: center;
    gap: 4px;
  }

  .instagram-highlights > div > span {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    background: #f4f4f4;
    border: 1px solid #d8d8d8;
    border-radius: 50%;
  }

  .instagram-highlights svg {
    width: 14px;
    height: 14px;
    stroke-width: 1.5;
  }

  .instagram-highlights small {
    font-size: 6px;
    font-weight: 680;
  }

  .instagram-tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 32px;
    border-top: 1px solid #efefef;
  }

  .instagram-tabs > span,
  .instagram-tabs > svg {
    place-self: center;
  }

  .instagram-tabs > svg {
    width: 15px;
    height: 15px;
    color: #9b9b9b;
    stroke-width: 1.4;
  }

  .instagram-grid-icon {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1px;
    width: 13px;
    height: 13px;
  }

  .instagram-grid-icon i {
    border: 1px solid #111111;
  }

  .instagram-photo-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2px;
    padding: 4px;
    border-radius: 0;
  }

  .instagram-photo {
    position: relative;
    aspect-ratio: 1;
    overflow: hidden;
    background: #eeeeee;
    transition:
      opacity 420ms ease,
      filter 420ms ease,
      transform 480ms cubic-bezier(.22, 1, .36, 1),
      box-shadow 420ms ease;
  }

  .instagram-photo.is-age-muted {
    opacity: .2;
    filter: blur(1.5px);
  }

  .instagram-photo.is-age-focused {
    z-index: 3;
    filter: blur(0);
    outline: 2px solid #0095f6;
    outline-offset: -3px;
    transform: scale(.96);
  }

  .instagram-photo img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .instagram-post-view {
    position: absolute;
    inset: 0;
    z-index: 8;
    background: #ffffff;
    opacity: 0;
    transform: translateX(18px);
    animation: instagram-post-open 440ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .instagram-post-topbar {
    display: grid;
    grid-template-columns: 32px 1fr 32px;
    align-items: center;
    height: 45px;
    padding: 0 12px;
    border-bottom: 1px solid #efefef;
    text-align: center;
  }

  .instagram-post-topbar span {
    font-size: 26px;
    line-height: 1;
    text-align: left;
  }

  .instagram-post-topbar strong {
    font-size: 12px;
    font-weight: 780;
  }

  .instagram-post-topbar i {
    font-size: 9px;
    font-style: normal;
    text-align: right;
  }

  .instagram-post-user {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 42px;
    padding: 0 12px;
    opacity: 1;
    filter: blur(0);
    animation: instagram-post-soften 420ms 1150ms ease forwards;
  }

  .instagram-post-user img {
    width: 25px;
    height: 25px;
    border-radius: 50%;
    object-fit: cover;
  }

  .instagram-post-user strong {
    font-size: 9px;
    font-weight: 760;
  }

  .instagram-post-image {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    opacity: 1;
    filter: blur(0);
    animation: instagram-post-soften 420ms 1150ms ease forwards;
  }

  .instagram-post-caption {
    position: relative;
    display: block;
    margin: 7px 12px 0;
    padding: 11px;
    background: #ffffff;
    box-shadow: 0 0 0 0 rgba(0, 149, 246, 0);
    border-radius: 9px;
    opacity: 1;
    transform: scale(1);
    animation: instagram-caption-focus 420ms 1150ms
      cubic-bezier(.22, 1, .36, 1) forwards;
  }

  .instagram-post-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 13px 3px;
    animation: instagram-post-soften 420ms 1150ms ease forwards;
  }

  .instagram-post-actions > div {
    display: flex;
    align-items: center;
    gap: 11px;
  }

  .instagram-post-actions svg {
    width: 18px;
    height: 18px;
    stroke-width: 1.65;
  }

  .instagram-post-likes {
    padding: 4px 13px 0;
    color: #333333;
    font-size: 8px;
    animation: instagram-post-soften 420ms 1150ms ease forwards;
  }

  .instagram-post-likes strong {
    color: #111111;
    font-weight: 760;
  }

  .instagram-post-caption p {
    margin: 0;
    color: #222222;
    font-size: 9px;
    line-height: 1.4;
  }

  .instagram-post-caption p strong {
    font-weight: 780;
  }

  .instagram-post-comments {
    display: grid;
    gap: 4px;
    padding: 7px 0px 0 12px;
    opacity: 0;
    animation:
      instagram-comments-in 320ms 420ms ease forwards,
      instagram-comments-soften 420ms 1150ms ease forwards;
  }

  .instagram-post-comments > span {
    color: #737373;
    font-size: 8px;
  }

  .instagram-post-comments p {
    margin: 0;
    color: #333333;
    font-size: 8px;
    line-height: 1.35;
  }

  .instagram-post-comments strong {
    color: #111111;
    font-weight: 760;
  }

  .prospect-game-slide {
    flex-direction: column;
    justify-content: center;
    padding-top: 26px;
    padding-bottom: 26px;
  }

  .prospect-game-heading {
    width: min(720px, 100%);
    text-align: left;
  }

  .prospect-game-eyebrow {
    display: block;
    margin-bottom: 9px;
    color: #777777;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: .15em;
  }

  .prospect-game-heading h1 {
    margin: 0;
    font-size: clamp(30px, 3.5vw, 42px);
    font-weight: 800;
    line-height: 1.02;
    letter-spacing: -.048em;
  }

  .prospect-game-heading p {
    max-width: 590px;
    margin: 9px 0 0;
    color: #707070;
    font-size: 12px;
    line-height: 1.5;
  }

  .prospect-game-body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
    width: min(720px, 100%);
    margin-top: 23px;
  }

  .mock-profile {
    width: 100%;
    animation: prospect-profile-in 480ms
      cubic-bezier(.22, 1, .36, 1) both;
  }

  .mock-profile-identity {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 0 11px;
    border-top: 1px solid #111111;
    border-bottom: 1px solid #dddddd;
  }

  .mock-profile-identity > img {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    object-fit: cover;
    border-radius: 50%;
  }

  .mock-profile h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -.04em;
  }

  .mock-profile header span {
    display: block;
    margin-top: 2px;
    color: #777777;
    font-size: 10px;
  }

  .mock-profile > p {
    margin: 0;
    max-width: 640px;
    padding: 14px 0 13px;
    box-sizing: border-box;
    color: #333333;
    font-size: 13px;
    line-height: 1.5;
  }

  .mock-profile-read {
    padding-bottom: 14px;
    border-bottom: 1px solid #111111;
  }

  .mock-profile-snapshot {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0;
  }

  .mock-profile-snapshot span {
    display: inline-flex;
    align-items: center;
    min-height: 25px;
    padding: 0 10px;
    background: #f1f1f1;
    border-radius: 999px;
    color: #3f3f3f;
    font-size: 9px;
    font-weight: 680;
    line-height: 1;
  }

  .prospect-decision {
    width: 100%;
  }

  .prospect-feedback h2 {
    max-width: 100%;
    margin: 0;
    font-size: clamp(21px, 2.4vw, 28px);
    font-weight: 800;
    line-height: 1;
    letter-spacing: -.043em;
  }

  .prospect-decision > p,
  .prospect-feedback p {
    max-width: 560px;
    margin: 14px 0 0;
    color: #666666;
    font-size: 12px;
    line-height: 1.48;
  }

  .prospect-choice-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    width: 100%;
    margin-top: 0;
  }

  .prospect-choice-row button,
  .prospect-feedback button,
  .prospect-results-slide button {
    appearance: none;
    min-height: 40px;
    padding: 0 16px;
    background: #ffffff;
    color: #111111;
    border: 1px solid #111111;
    border-radius: 10px;
    cursor: pointer;
    font: inherit;
    font-size: 11px;
    font-weight: 750;
    transition:
      background 180ms ease,
      color 180ms ease,
      transform 180ms ease;
  }

  .prospect-choice-row button {
    display: grid;
    grid-template-columns: 34px 1fr;
    align-items: center;
    gap: 11px;
    min-height: 50px;
    padding: 7px 11px;
    font-size: 12px;
    text-align: left;
  }

  .prospect-choice-row button:hover,
  .prospect-feedback button:hover,
  .prospect-results-slide button:hover {
    transform: translateY(-1px);
  }

  .prospect-feedback button,
  .prospect-results-slide button {
    background: #111111;
    color: #ffffff;
  }

  .prospect-choice-row button > span {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    background: #f2f2f2;
    border-radius: 8px;
    color: #111111;
    font-size: 10px;
    font-weight: 800;
  }

  .prospect-choice-row button > strong {
    font-size: 12px;
    font-weight: 750;
  }

  .prospect-choice-row button:hover {
    background: #f7f7f7;
  }

  .prospect-feedback-heading {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .prospect-feedback-heading i {
    display: grid;
    place-items: center;
    width: 29px;
    height: 29px;
    flex: 0 0 29px;
    border: 1.5px solid currentColor;
    border-radius: 50%;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
  }

  .prospect-feedback.is-correct .prospect-feedback-heading i {
    color: #198754;
  }

  .prospect-feedback.is-incorrect .prospect-feedback-heading i {
    color: #b44740;
  }

  .prospect-feedback-heading h2 {
    margin: 0;
  }

  .prospect-feedback button {
    margin-top: 16px;
  }

  .prospect-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 9px;
    margin-top: 16px;
  }

  .prospect-pagination > span {
    width: 6px;
    height: 6px;
    flex: 0 0 6px;
    background: #d2d2d2;
    border-radius: 999px;
    transition:
      width 360ms cubic-bezier(.22, 1, .36, 1),
      flex-basis 360ms cubic-bezier(.22, 1, .36, 1),
      background 260ms ease;
  }

  .prospect-pagination > span.is-current {
    width: 22px;
    flex-basis: 22px;
    background: #111111;
  }

  .prospect-results-slide {
    flex-direction: column;
    text-align: center;
  }

  .prospect-results-count {
    display: flex;
    align-items: baseline;
  }

  .prospect-results-count strong {
    font-size: clamp(64px, 9vw, 96px);
    font-weight: 800;
    line-height: .82;
    letter-spacing: -.075em;
  }

  .prospect-results-count span {
    margin-left: 7px;
    color: #777777;
    font-size: clamp(17px, 2.3vw, 26px);
    font-weight: 500;
  }

  .prospect-results-slide h1 {
    margin: 20px 0 0;
    font-size: clamp(27px, 3.2vw, 38px);
    font-weight: 800;
    line-height: 1.04;
    letter-spacing: -.043em;
  }

  .prospect-results-slide p {
    max-width: 520px;
    margin: 12px 0 0;
    color: #666666;
    font-size: 12px;
    line-height: 1.5;
  }

  .prospect-results-slide button {
    margin-top: 18px;
  }

  @keyframes prospect-profile-in {
    from {
      opacity: 0;
      transform: translateY(12px) scale(.99);
    }

    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes overview-word-reveal {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes overview-intro-enter {
    from {
      opacity: 0;
      transform: translateY(14px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes overview-stack-enter {
    from {
      opacity: 0;
      transform: translateY(18px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes overview-current-step-in {
    from {
      opacity: 0;
      transform: translateY(18px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes overview-segment-progress {
    to {
      transform: scaleY(.55);
    }
  }

  @keyframes target-intro-word-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes target-review-heading-in {
    to {
      opacity: 1;
    }
  }

  @keyframes target-review-item-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes review-divider-vertical {
    to {
      transform: scaleY(1);
    }
  }

  @keyframes review-divider-horizontal {
    to {
      transform: scaleX(1);
    }
  }

  @keyframes trace-head-pulse {
    0% {
      opacity: .7;
      transform: scale(.55);
    }

    75%,
    100% {
      opacity: 0;
      transform: scale(2.15);
    }
  }

  @keyframes la-marker-in {
    from {
      opacity: 0;
      transform: scale(.25);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes la-pulse {
    0% {
      opacity: .7;
      transform: scale(.65);
    }

    70%,
    100% {
      opacity: 0;
      transform: scale(2.2);
    }
  }

  @keyframes la-callout-draw {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes la-callout-text-in {
    from {
      opacity: 0;
      transform: translateY(4px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes age-numbers-in {
    from {
      opacity: 0;
      transform: scaleX(1.18);
    }

    to {
      opacity: 1;
      transform: scaleX(1);
    }
  }

  @keyframes age-line-in {
    to {
      transform: scaleX(1);
    }
  }

  @keyframes age-dot-in {
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes ratio-row-in {
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes ratio-line-in {
    to {
      width: var(--ratio-width);
    }
  }

  @keyframes life-word-left {
    from {
      opacity: 0;
      transform: translateX(-34px);
    }

    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes life-word-right {
    from {
      opacity: 0;
      transform: translateX(34px);
    }

    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes life-divider-in {
    to {
      opacity: 1;
    }
  }

  @keyframes life-illustration-left {
    to {
      opacity: 1;
      transform: translateX(0) rotate(-3deg)
        scale(1);
    }
  }

  @keyframes life-illustration-right {
    to {
      opacity: 1;
      transform: translateX(0) rotate(3deg)
        scale(1);
    }
  }

  @keyframes life-cancel-draw {
    to {
      transform: rotate(43deg) scaleX(1);
    }
  }

  @keyframes life-stage-statement-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes life-stage-highlight-in {
    to {
      transform: rotate(-.7deg) scaleX(1);
    }
  }

  @keyframes life-stage-highlight-soft-in {
    to {
      transform: rotate(.35deg) scaleX(1);
    }
  }

  @keyframes threshold-filter-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes threshold-line-in {
    to {
      width: min(520px, 78vw);
    }
  }

  @keyframes threshold-title-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fit-reason-scene-in {
    from {
      opacity: 0;
      transform: translateY(14px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fit-final-block-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fit-summary-word-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes reason-copy-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes reason-title-in {
    to {
      opacity: 1;
      clip-path: inset(0 0 0 0);
    }
  }

  @keyframes fit-visual-in {
    from {
      opacity: 0;
      transform: translateY(12px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fit-note-in {
    to {
      opacity: 1;
      transform: translateY(0) rotate(-3deg);
    }
  }

  @keyframes fit-ink-draw {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes fit-suitcase-arrive {
    from {
      opacity: 0;
      transform: translateX(-110px) rotate(-2deg);
    }

    72% {
      opacity: 1;
      transform: translateX(5px) rotate(.5deg);
    }

    to {
      opacity: 1;
      transform: translateX(0) rotate(0);
    }
  }

  @keyframes fit-tag-sway {
    0% {
      transform: rotate(-14deg);
    }

    42% {
      transform: rotate(8deg);
    }

    72% {
      transform: rotate(-4deg);
    }

    to {
      transform: rotate(0);
    }
  }

  @keyframes fit-word-reveal {
    to {
      opacity: 1;
      filter: blur(0);
      transform: translateY(0);
    }
  }

  @keyframes fit-caption-window {
    0%,
    8% {
      opacity: 0;
      transform: translateY(18px) scale(.985);
    }

    18%,
    78% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }

    100% {
      opacity: 0;
      transform: translateY(-18px) scale(.985);
    }
  }

  @keyframes fit-person-in {
    from {
      opacity: 0;
      transform: translateY(18px) rotate(-1deg);
    }

    to {
      opacity: 1;
      transform: translateY(0) rotate(0);
    }
  }

  @keyframes fit-lanyard-drop {
    from {
      opacity: 0;
      transform: translateY(-68px) rotate(-7deg);
    }

    62% {
      opacity: 1;
      transform: translateY(5px) rotate(4deg);
    }

    to {
      opacity: 1;
      transform: translateY(0) rotate(0);
    }
  }

  @keyframes fit-badge-sway {
    0% {
      transform: rotate(-9deg);
    }

    42% {
      transform: rotate(7deg);
    }

    72% {
      transform: rotate(-3deg);
    }

    to {
      transform: rotate(0);
    }
  }

  @keyframes fit-motion-in {
    0%,
    100% {
      opacity: 0;
    }

    35%,
    72% {
      opacity: .58;
    }
  }

  @keyframes fit-journal-in {
    from {
      opacity: 0;
      transform: translateY(18px) rotate(-1deg);
    }

    to {
      opacity: 1;
      transform: translateY(0) rotate(0);
    }
  }

  @keyframes fit-page-turn {
    0% {
      transform: perspective(580px)
        rotateY(0deg) skewY(0);
    }

    52% {
      transform: perspective(580px)
        rotateY(78deg) skewY(-2deg);
    }

    to {
      transform: perspective(580px)
        rotateY(176deg) skewY(0);
    }
  }

  @keyframes fit-table-in {
    from {
      opacity: 0;
      transform: scale(.94);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes fit-chair-in {
    from {
      opacity: 0;
      transform: translateY(12px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fit-fourth-chair-in {
    from {
      opacity: 0;
      transform: translateY(78px) rotate(3deg);
    }

    72% {
      opacity: 1;
      transform: translateY(-4px) rotate(-1deg);
    }

    to {
      opacity: 1;
      transform: translateY(0) rotate(0);
    }
  }

  @keyframes refined-final-title {
    from {
      opacity: 0;
      transform: translateY(16px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes refined-criterion-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fit-meta-expand {
    from {
      opacity: 0;
      letter-spacing: .02em;
    }

    to {
      opacity: 1;
      letter-spacing: .09em;
    }
  }

  @keyframes fit-title-rise {
    from {
      opacity: 0;
      transform: translateY(22px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes comfort-compress {
    from {
      opacity: 0;
      letter-spacing: .08em;
      transform: scaleX(1.18);
    }

    to {
      opacity: 1;
      letter-spacing: -.075em;
      transform: scaleX(1);
    }
  }

  @keyframes comfort-expand {
    from {
      opacity: 0;
      letter-spacing: -.02em;
    }

    to {
      opacity: 1;
      letter-spacing: .16em;
    }
  }

  @keyframes comfort-line {
    to {
      width: 82%;
    }
  }

  @keyframes access-label-in {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }

    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes access-wipe-in {
    to {
      clip-path: inset(0 0 0 0);
    }
  }

  @keyframes access-note-in {
    to {
      opacity: 1;
    }
  }

  @keyframes final-from-left {
    from {
      opacity: 0;
      transform: translateX(-28px);
    }

    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes final-from-bottom {
    from {
      opacity: 0;
      transform: translateY(22px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes final-from-right {
    from {
      opacity: 0;
      transform: translateX(28px);
    }

    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes final-lock-in {
    from {
      opacity: 0;
      transform: scale(.9);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes profile-copy-in {
    from {
      opacity: 0;
      clip-path: inset(0 100% 0 0);
      transform: translateX(-18px);
    }

    to {
      opacity: 1;
      clip-path: inset(0 0 0 0);
      transform: translateX(0);
    }
  }

  @keyframes scan-line-in {
    to {
      transform: scaleX(1);
    }
  }

  @keyframes instagram-post-open {
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes instagram-caption-focus {
    to {
      opacity: 1;
      box-shadow: 0 0 0 2px #0095f6;
      transform: scale(1.015);
    }
  }

  @keyframes instagram-post-soften {
    to {
      opacity: .38;
      filter: blur(1.3px);
    }
  }

  @keyframes instagram-comments-in {
    to {
      opacity: 1;
    }
  }

  @keyframes instagram-comments-soften {
    to {
      opacity: .3;
      filter: blur(.7px);
    }
  }

  @media (max-width: 720px) {
    .who-slide {
      min-height: 450px;
      padding: 26px 16px;
    }

    .profile-scan-slide {
      min-height: 700px;
      overflow: visible;
    }

    .overview-intro-beat {
      width: 100%;
      font-size: clamp(32px, 8.8vw, 44px);
    }

    .overview-step-stack {
      width: 100%;
      min-height: 330px;
    }

    .overview-step-row {
      grid-template-columns: 24px minmax(0, 1fr);
      gap: 14px;
      padding: 8px 0;
    }

    .overview-step-marker {
      width: 22px;
      height: 22px;
    }

    .overview-step-row.is-current {
      min-height: 100px;
    }

    .overview-step-row.is-current .overview-step-copy strong {
      font-size: clamp(34px, 9.4vw, 47px);
    }

    .overview-step-stack.is-reviewing .overview-step-copy strong {
      font-size: clamp(22px, 6.3vw, 31px);
    }

    .target-scene-stage {
      height: 370px;
    }

    .target-intro h1 {
      font-size: 40px;
    }

    .target-intro-hand-group {
      font-size: 1.06em;
      word-spacing: -.13em;
    }

    .target-review h2 {
      margin-bottom: 14px;
    }

    .target-review-quadrant {
      min-height: 118px;
      padding: 18px;
    }

    .target-review-quadrant > svg {
      width: 16px;
      height: 16px;
      margin-bottom: 12px;
    }

    .target-review-quadrant > strong {
      font-size: 18px;
    }

    .target-review-quadrant > small {
      font-size: 7px;
    }

    .la-map {
      width: 112px;
    }

    .age-visual {
      width: min(82vw, 350px);
    }

    .age-range-copy strong {
      font-size: 54px;
    }

    .audience-ratio {
      width: min(86vw, 390px);
    }

    .ratio-row {
      grid-template-columns: 21px 1fr 42px 44px;
      gap: 8px;
    }

    .ratio-row > svg {
      width: 20px;
      height: 20px;
    }

    .life-stage-statement {
      font-size: clamp(31px, 8.6vw, 43px);
    }

    .fit-kinetic-intro {
      min-height: 300px;
    }

    .fit-kinetic-filters {
      gap: 16px;
      white-space: nowrap;
    }

    .fit-kinetic-intro.show-connect
    .fit-kinetic-filters {
      transform: translateX(-50%) scale(.72);
    }

    .fit-starting-copy > strong {
      font-size: 38px;
    }

    .fit-connect-copy > strong {
      font-size: 52px;
    }

    .fit-reason-stage {
      min-height: 430px;
    }

    .fit-reason-card {
      width: 100%;
    }

    .fit-reason-card-inner {
      grid-template-columns: 1fr;
      gap: 10px;
    }

    .fit-reason-stage.has-history
    .fit-reason-card.is-active {
      left: 58%;
      width: calc(100% - 90px);
    }

    .fit-reason-card.is-complete {
      left: 58px;
    }

    .fit-reason-card.is-complete
    .fit-reason-card-inner {
      transform: scale(.18);
    }

    .fit-final-summary {
      grid-template-columns: 1fr;
      gap: 22px;
      width: 92%;
    }

    .fit-final-list {
      gap: 7px;
    }

    .fit-final-list-item {
      grid-template-columns: 25px minmax(0, 1fr);
      gap: 10px;
      min-height: 32px;
    }

    .fit-final-list-item strong {
      font-size: 10px;
      text-align: left;
    }

    .fit-final-divider {
      width: 100%;
      height: 1px;
    }

    .fit-final-message {
      font-size: clamp(32px, 8vw, 46px);
      text-align: center;
    }

    .fit-reason-focus {
      grid-template-columns: 1fr;
      gap: 10px;
      margin-top: 14px;
    }

    .fit-reason-copy {
      justify-items: center;
      text-align: center;
    }

    .fit-reason-copy strong {
      max-width: 460px;
      font-size: 34px;
    }

    .fit-reason-copy p {
      max-width: 390px;
      margin-top: 8px;
    }

    .fit-reason-visual {
      min-height: 220px;
    }

    .fit-editorial-art {
      height: 220px;
    }

    .fit-hand-note {
      font-size: 12px;
    }

    .fit-la-note,
    .fit-id-note,
    .fit-phase-note,
    .fit-circle-note {
      right: 14%;
      bottom: 7px;
    }

    .fit-final-criteria {
      gap: 14px;
    }

    .fit-final-criteria span {
      font-size: 8px;
    }

    .profile-scan-layout {
      grid-template-columns: 1fr;
      gap: 24px;
      width: min(460px, 100%);
    }

    .profile-scan-copy {
      text-align: center;
    }

    .profile-scan-copy strong,
    .profile-scan-copy p {
      margin-right: auto;
      margin-left: auto;
    }

    .profile-scan-copy strong {
      font-size: 31px;
    }

    .profile-scan-copy p {
      margin-top: 10px;
      font-size: 11px;
    }

    .scan-profile-card {
      width: min(300px, 100%);
      height: 520px;
      margin: 0 auto;
    }

    .prospect-game-slide {
      justify-content: center;
      min-height: 610px;
      overflow: visible;
    }

    .prospect-game-body {
      gap: 18px;
    }

    .mock-profile,
    .prospect-decision {
      width: min(100%, 500px);
      margin: 0 auto;
    }

    .prospect-choice-row {
      display: grid;
      grid-template-columns: 1fr;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .who-slide,
    .who-slide * {
      animation-duration: 1ms !important;
      animation-delay: 0ms !important;
      transition-duration: 1ms !important;
    }
  }
`;

WhoToInvite.title = 'Who to look for';
WhoToInvite.steps = STEPS;
